<template>
  <div class="user-auth-container">
    <div class="auth-sky" aria-hidden="true"></div>
    <div class="auth-stars" aria-hidden="true"></div>
    <div class="auth-vignette" aria-hidden="true"></div>

    <div class="auth-wrap">
      <div class="auth-topbar me-topbar">
        <div class="brand">
          <div class="brand-block"></div>
          <div class="brand-text">
            <div class="brand-title">云边小镇</div>
            <div class="brand-subtitle">MY SPACE</div>
          </div>
        </div>

        <div class="topbar-actions">
          <div class="topbar-pill" :class="{ 'active-pill': isNotificationsPanelActive }" @click="isNotificationsPanelActive = !isNotificationsPanelActive" role="button" tabindex="0">
            <span class="pill-icon">🔔</span>
            <span class="pill-text">通知</span>
            <span v-if="unreadCount > 0" class="pill-badge">{{ unreadCount }}</span>
          </div>

          <div class="topbar-pill" :class="{ 'active-pill': isProfilePanelActive }" @click="isProfilePanelActive = !isProfilePanelActive" role="button" tabindex="0">
            <img :src="avatarSrc" class="pill-avatar" alt="avatar" />
            <span class="pill-text">{{ profile.nickname || '个人中心' }}</span>
          </div>

          <a class="back-home" href="/index">返回首页</a>
        </div>
      </div>

      <!-- 右侧抽屉式面板：个人中心 -->
      <div class="me-drawer-panel profile-drawer" :class="{ active: isProfilePanelActive }">
        <div class="drawer-header">
          <div class="drawer-title">个人中心</div>
          <button class="drawer-close" @click="isProfilePanelActive = false">×</button>
        </div>
        <div class="drawer-body">
          <div class="profile">
            <div class="avatar-box" @click="onAvatarClick">
              <img :src="avatarSrc" class="avatar" alt="avatar" />
              <div class="avatar-tip">点击上传头像</div>
            </div>

            <div class="profile-info">
              <div class="kv">
                <div class="k">游戏昵称</div>
                <div class="v">{{ profile.nickname || '-' }}</div>
              </div>
              <div class="kv">
                <div class="k">邮箱</div>
                <div class="v">{{ profile.email || '-' }}</div>
              </div>

              <div class="row profile-actions">
                <button class="mc-btn mc-btn-primary" type="button" @click="openEmailModal">修改邮箱</button>
                <button class="mc-btn mc-btn-primary" type="button" @click="openPasswordModal">修改密码</button>
              </div>
              <div class="row">
                <button class="mc-btn mc-btn-danger" type="button" @click="logout">退出登录</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧抽屉式面板：通知中心 -->
      <div class="me-drawer-panel notifications-drawer" :class="{ active: isNotificationsPanelActive }">
        <div class="drawer-header">
          <div class="drawer-title">通知中心</div>
          <button class="drawer-close" @click="isNotificationsPanelActive = false">×</button>
        </div>
        <div class="drawer-body">
          <div class="data-list">
            <div v-if="notifications.length === 0" class="data-item" style="justify-content:center; color:#bbb;">暂无通知</div>
            <div v-for="n in notifications" :key="n.id" class="data-item" :class="{ 'data-item-unread': n.isRead === 0 }" style="align-items:flex-start;">
              <div class="item-content" style="gap: 10px;">
                <div class="item-info" style="gap: 6px;">
                  <div class="item-text" style="display:flex; align-items:center; gap: 8px;">
                    <span v-if="n.isRead === 0" class="dot"></span>
                    <span>{{ n.title || '通知' }}</span>
                  </div>
                  <div class="item-meta" style="color:#9ae054;">{{ formatDateTime(n.createdAt) }}</div>
                  <div class="item-text" style="color:#fff; white-space: pre-wrap;">
                    <template v-if="n.type === 'building_updated_by_admin' || n.type === 'building_deleted_by_admin' || n.type === 'user_notice_updated_by_admin'">
                      原因：{{ n.content }}
                    </template>
                    <template v-else>
                      {{ n.content }}
                    </template>
                  </div>
                </div>
              </div>
              <div class="item-actions" style="flex-wrap: wrap; justify-content:flex-end;">
                <button v-if="n.relatedBuildingId" class="mc-btn-small" @click="handleBuildingNotificationClick(n)">查看建筑</button>
                <button v-if="n.isRead === 0" class="mc-btn-small mc-btn-primary" @click="markRead(n.id)">标记已读</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== 全屏两栏主舞台布局 ===== -->
      <div class="me-layout-full">
        <div class="me-hero">
          <div class="me-hero-left">
            <div class="me-hero-avatar">
              <img :src="avatarSrc" alt="avatar" />
            </div>
            <div class="me-hero-text">
              <div class="me-hero-title">欢迎回来，{{ profile.nickname || '旅行者' }}</div>
              <div class="me-hero-sub">感谢你支持云边小镇！欢迎使用云边小镇用户系统。在这里发布公告、管理建筑，并查看你的通知动态。</div>
              <div class="me-hero-tags">
                <span class="me-tag">公告 {{ myNotices.length }}</span>
                <span class="me-tag">建筑 {{ myBuildings.length }}</span>
                <span class="me-tag">未读 {{ unreadCount }}</span>
              </div>
            </div>
          </div>
          <div class="me-hero-actions">
            <button class="mc-btn mc-btn-primary" type="button" @click="openNoticeModalForCreate" :disabled="remainingNoticeCount<=0">发布公告</button>
            <button class="mc-btn mc-btn-primary" type="button" @click="openBuildModal">新建建筑</button>
          </div>
        </div>

        <div class="layout-main">
          <!-- 左侧：公告 + 建筑（主内容） -->
          <div class="layout-left">
            <!-- 公告管理 -->
            <div class="me-card">
              <div class="me-card-head">
                <div>
                  <div class="me-card-title">公告管理</div>
                  <div class="me-card-sub">最多 5 条｜每条最多 300 字｜有效期 7 天</div>
                </div>
                <div class="me-card-head-actions">
                  <button class="mc-btn mc-btn-primary" type="button" @click="openNoticeModalForCreate" :disabled="remainingNoticeCount<=0">发布公告</button>
                </div>
              </div>

              <div class="me-card-body">
                <div class="me-inline-hint">剩余可发布：<b>{{ remainingNoticeCount }}</b></div>

                <div v-if="myNotices.length === 0" class="me-empty">
                  <div class="me-empty-title">暂无公告</div>
                  <div class="me-empty-sub">发布一条公告，让小镇居民第一时间知道你的消息。</div>
                </div>

                <div v-else class="me-notice-list">
                  <div v-for="n in myNotices" :key="n.id" class="me-notice-item">
                    <div class="me-notice-left">
                      <div class="me-notice-pin"></div>
                      <div class="me-notice-icon">{{ getNoticeIcon(n) }}</div>
                    </div>
                    <div class="me-notice-body">
                      <div class="me-notice-title">{{ n.content }}</div>
                      <div class="me-notice-meta">
                        <span class="me-badge" :class="remainingBadgeClass(n.expireAt)">剩余 {{ formatRemaining(n.expireAt) }}</span>
                        <span class="me-meta">到期 {{ formatDateTime(n.expireAt) }}</span>
                      </div>
                      <div class="me-notice-actions">
                        <button class="mc-btn-small" @click="editNotice(n)">编辑</button>
                        <button class="mc-btn-small mc-btn-danger" @click="deleteNotice(n.id)">删除</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          <!-- 建筑管理 -->
          <div class="me-card">
            <div class="me-card-head">
              <div>
                <div class="me-card-title">建筑管理</div>
                 <div class="me-card-sub">1.建筑名称不能大于15个字，建筑副标题不能大于20个字</div>
                <div class="me-card-sub">2.发布建筑的图片必须大于等于3张且不能超过10张。</div>
                <div class="me-card-sub">3.建筑描述必须大于100字，请详细描述你的建筑噢~</div>
              </div>
              <div class="me-card-head-actions">
                <button class="mc-btn mc-btn-primary" type="button" @click="openBuildModal">新建建筑</button>
              </div>
            </div>

            <div class="me-card-body">
              <div v-if="myBuildings.length === 0" class="me-empty">
                <div class="me-empty-icon">🏗️</div>
                <div class="me-empty-title">还没有建筑</div>
                <div class="me-empty-sub">像放置方块一样，创建你的第一个建筑吧！</div>
              </div>

              <div v-else class="me-building-list">
                <div v-for="b in myBuildings" :key="'mb-' + (b.buildingId || b.id)" class="me-building-item">
                  <div class="me-building-cover" @click="goBuilding(b.buildingId || b.id)">
                    <img v-if="b.coverImage" :src="b.coverImage" alt="cover" />
                    <div v-else class="me-building-cover-placeholder">无封面</div>
                    <div class="me-building-cover-frame"></div>
                    <div class="me-building-cover-badge">已发布</div>
                  </div>
                  <div class="me-building-body">
                    <div class="me-building-title"><span class="mc-item-icon">{{ getBuildingIcon(b) }}</span>{{ b.name }}</div>
                    <div class="me-building-sub">{{ b.subtitle }}</div>
                    <div class="me-building-actions">
                      <button class="mc-btn-small" @click="goBuilding(b.buildingId || b.id)">查看</button>
                      <button class="mc-btn-small" @click="openEditBuildModal(b)">编辑</button>
                      <button class="mc-btn-small mc-btn-danger" @click="deleteMyBuilding(b.buildingId || b.id)">删除</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

          <!-- 右侧：数据看板（辅助信息） -->
          <div class="layout-right">
            <div class="me-card me-card-compact">
              <div class="me-card-title">我的信息</div>
              <div class="me-card-sub">快速查看你的状态</div>
              <div class="stat-grid">
                <div class="stat">
                  <div class="stat-k">公告</div>
                  <div class="stat-v">{{ myNotices.length }}</div>
                </div>
                <div class="stat">
                  <div class="stat-k">建筑</div>
                  <div class="stat-v">{{ myBuildings.length }}</div>
                </div>
                <div class="stat">
                  <div class="stat-k">未读</div>
                  <div class="stat-v">{{ unreadCount }}</div>
                </div>
              </div>
            </div>

            <!-- 玩家状态栏（背包栏风格） -->
            <div class="me-card me-card-compact me-player-status">
              <div class="me-card-title">玩家状态</div>
              <div class="me-card-sub">血量 · 饱食度 · 经验</div>
              <div class="mc-status-bars">
                <div class="mc-status-bar health">
                  <div class="mc-status-label">❤️ 血量</div>
                  <div class="mc-status-track">
                    <div class="mc-status-fill" :style="{ width: Math.min(playerHealth, 100) + '%' }"></div>
                  </div>
                  <div class="mc-status-value">{{ Math.round(Math.min(playerHealth, 100) / 100 * 20) }}/20</div>
                </div>
                <div class="mc-status-bar hunger">
                  <div class="mc-status-label">🍖 饱食度</div>
                  <div class="mc-status-track">
                    <div class="mc-status-fill" :style="{ width: Math.min(playerHunger, 100) + '%' }"></div>
                  </div>
                  <div class="mc-status-value">{{ Math.round(Math.min(playerHunger, 100) / 100 * 20) }}/20</div>
                </div>
                <div class="mc-status-bar exp">
                  <div class="mc-status-label">⭐ 经验</div>
                  <div class="mc-status-track">
                    <div class="mc-status-fill" :style="{ width: playerExpPercent + '%' }"></div>
                  </div>
                  <div class="mc-status-value">Lv.{{ playerLevel }}</div>
                </div>
              </div>
            </div>

            <div class="me-card me-card-clock">
              <div class="me-card-title">时间</div>
              <div class="me-card-sub">现实时间 · Minecraft 时钟</div>
              <div class="mc-clock-wrap">
                <div class="mc-clock-outer">
                  <div class="dial" :style="{ transform: `translateY(${mcClockDialY}px)` }">
                    <div class="moon"></div>
                    <div class="sun"></div>
                    <div class="moon"></div>
                  </div>
                </div>
                <div class="mc-clock-time">{{ currentTimeText }}</div>
                <div class="mc-clock-status">{{ mcClockStatus }}</div>
              </div>
            </div>

            <!-- 今日运势 / 天气 / 生物出没 -->
            <div class="me-card me-card-compact me-daily">
              <div class="me-card-title">今日冒险情报</div>
              <div class="me-card-sub">运势 · 天气 · 生物出没</div>
              <div class="mc-daily-grid">
                <div class="mc-daily-item">
                  <div class="mc-daily-k">🎲 运势</div>
                  <div class="mc-daily-v">{{ dailyFortune.text }}</div>
                </div>
                <div class="mc-daily-item">
                  <div class="mc-daily-k">☁️ 天气</div>
                  <div class="mc-daily-v">{{ dailyWeather.text }}</div>
                </div>
                <div class="mc-daily-item" style="grid-column: 1 / -1;">
                  <div class="mc-daily-k">🧟 出没</div>
                  <div class="mc-daily-v">{{ dailyMobs.text }}</div>
                </div>
              </div>
            </div>

            <div class="me-card me-tip">
              <div class="me-card-title">小贴士</div>
              <div class="me-tip-text">
                <div>1. 请遵守国家的法律法规，不可转播淫秽低俗信息、诈骗信息、以及涉及国家不当的政治言论。</div>
                <div>2. 不可以发布透露自己有关的个人信息，包括但不限于QQ号和微信号以及手机电话号码。</div>
                <div>3. 不可以打广告，发布不实信息，以及未知链接。</div>
                <div>4. 如果有违反云边小镇主页规定的成员，轻则警告，再者封禁账号，重则移交公安部门进行处理。</div>
                <div></div>
                <div>本网站仅限小镇成员使用，祝你开心~(*^▽^*)~</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <teleport to="body">
        <div class="mc-modal-mask" :class="{ active: noticeModalActive }" @click.self="closeNoticeModal">
          <div class="mc-modal notice-modal" :class="{ active: noticeModalActive }">
            <h3 style="color: #000; margin: 0; font-size: 14px;">{{ editingNoticeId ? '编辑公告' : '发布公告' }}</h3>
            <div class="mc-modal-scroll">
              <textarea
                class="mc-input"
                rows="4"
                v-model.trim="noticeDraft"
                @blur="validateNoticeDraft"
                placeholder="写下你想告诉小镇居民的一句话..."
              ></textarea>
              <div class="hint" style="color: rgba(0,0,0,0.45); text-align: right; margin-top: 4px;">{{ noticeDraft.length }}/300</div>
            </div>

            <div class="modal-btns">
              <button class="mc-btn" @click="closeNoticeModal">取消</button>
              <button class="mc-btn mc-btn-primary" :disabled="noticeSubmitting" @click="submitNotice">{{ noticeSubmitting ? (editingNoticeId ? '保存中...' : '发布中...') : (editingNoticeId ? '确认修改' : '确认发布') }}</button>
            </div>
          </div>
        </div>
      </teleport>

      <teleport to="body">
        <div class="mc-modal-mask" :class="{ active: buildModalActive }" @click.self="closeBuildModal">
          <div class="mc-modal building-modal" :class="{ active: buildModalActive }">
            <h3 style="color: #000; margin: 0; font-size: 14px;">{{ editingMyBuilding ? '编辑建筑' : '新建建筑' }}</h3>
            <div class="mc-modal-scroll">
              <div style="position: relative;">
                <input v-model="buildForm.name" type="text" class="mc-input" placeholder="建筑名称" @input="validateBuildingName" />
                <div class="hint" style="color: rgba(0,0,0,0.45);">{{ (buildForm.name || '').length }}/15</div>
              </div>
              <div style="position: relative;">
                <input v-model="buildForm.subtitle" type="text" class="mc-input" placeholder="副标题" @input="validateBuildingSubtitle" />
                <div class="hint" style="color: rgba(0,0,0,0.45);">{{ (buildForm.subtitle || '').length }}/20</div>
              </div>
              <div class="date-input-group">
                <div v-if="buildForm.date !== 'unknown'" class="mc-datepicker-container">
                  <input 
                    v-model="buildForm.dateValue" 
                    type="text" 
                    class="mc-input" 
                    readonly
                    placeholder="年 / 月 / 日" 
                    @click="toggleDatePicker"
                  />
                  <div v-if="showDatePicker" class="mc-datepicker-panel">
                    <div class="mc-datepicker-header">
                      <button type="button" class="mc-datepicker-btn" @click="prevYear" title="上一年">&lt;&lt;</button>
                      <button type="button" class="mc-datepicker-btn" @click="prevMonth" title="上个月">&lt;</button>
                      <span class="mc-datepicker-title">{{ currentYear }}年 {{ currentMonth + 1 }}月</span>
                      <button type="button" class="mc-datepicker-btn" @click="nextMonth" title="下个月">&gt;</button>
                      <button type="button" class="mc-datepicker-btn" @click="nextYear" title="下一年">&gt;&gt;</button>
                    </div>
                    <div class="mc-datepicker-weekdays">
                      <span v-for="day in ['日','一','二','三','四','五','六']" :key="day">{{ day }}</span>
                    </div>
                    <div class="mc-datepicker-days">
                      <span 
                        v-for="empty in firstDayOfMonth" 
                        :key="'empty-'+empty" 
                        class="mc-datepicker-day empty"
                      ></span>
                      <span 
                        v-for="day in daysInMonth" 
                        :key="day" 
                        class="mc-datepicker-day"
                        :class="{ active: isSelected(day) }"
                        @click="selectDate(day)"
                      >
                        {{ day }}
                      </span>
                    </div>
                    <div class="mc-datepicker-footer">
                      <button type="button" class="mc-datepicker-footer-btn" @click="selectToday">TODAY</button>
                      <button type="button" class="mc-datepicker-footer-btn clear" @click="clearDate">CLEAR</button>
                    </div>
                  </div>
                </div>
                <input 
                  v-else
                  type="text" 
                  class="mc-input" 
                  value="建成时间未知" 
                  disabled
                />
                <button 
                  type="button" 
                  class="mc-unknown-btn" 
                  :class="{ active: buildForm.date === 'unknown' }"
                  @click="buildForm.date = (buildForm.date === 'unknown' ? '' : 'unknown'); buildForm.dateValue = ''; showDatePicker = false"
                  title="不知道具体的建成时间？那就填未知吧~"
                >
                  <span class="mc-unknown-icon">?</span>
                  <span class="mc-unknown-text">未知</span>
                </button>
              </div>
              <input v-model="buildForm.author" type="text" class="mc-input" placeholder="建筑师" />

              <div class="mc-modal-body" style="padding: 0; border: none; background: transparent; box-shadow: none;">
                <label style="color: #000; font-size: 12px; display: block; margin-bottom: 5px;">选择轮播图片（可多选）：</label>
                <input type="file" @change="handleBuildImagesChange" accept="image/*" multiple style="margin-bottom: 10px; width: 100%;" />
                <div v-if="buildForm.images && buildForm.images.length > 0" class="mc-image-grid">
                  <div
                    v-for="(imageUrl, index) in buildForm.images"
                    :key="imageUrl + '-' + index"
                    class="mc-image-tile"
                    :class="{ 'mc-image-tile-cover': index === 0 }"
                    draggable="true"
                    @dragstart="handleImageDragStart($event, index)"
                    @dragover.prevent="handleImageDragOver"
                    @drop="handleImageDrop($event, index)"
                  >
                    <div v-if="index === 0" class="mc-image-cover-label">封面图</div>
                    <img :src="imageUrl" class="mc-image-thumb" @click="openImagePreview(imageUrl)" />
                    <button type="button" class="mc-image-del" @click="removeBuildImage(index)">删除</button>
                  </div>
                </div>
                <div v-if="buildUploadQueue.length > 0" class="mc-image-uploading">
                  <div v-for="item in buildUploadQueue" :key="item.id" class="mc-image-uploading-item">
                    <img :src="item.localUrl" />
                    <div class="mc-image-uploading-label">
                      <span v-if="item.status === 'uploading'">上传中...</span>
                      <span v-else-if="item.status === 'error'" style="color: #ff6b6b;">失败</span>
                      <span v-else-if="item.status === 'success'" style="color: #5ea818;">成功</span>
                    </div>
                  </div>
                </div>
              </div>

              <textarea v-model="buildForm.descriptionJson" class="mc-input" rows="5" placeholder="建筑描述（每行一个段落）"></textarea>
              <div class="hint" style="color: rgba(0,0,0,0.45);">{{ (buildForm.descriptionJson || '').trim().length }}/5000</div>
            </div>

            <div class="modal-btns">
              <button class="mc-btn" @click="closeBuildModal">取消</button>
              <button class="mc-btn mc-btn-primary" :disabled="buildSubmitting" @click="submitBuild">{{ buildSubmitting ? (editingMyBuilding ? '修改中...' : '发布中...') : (editingMyBuilding ? '确认修改' : '确认发布') }}</button>
            </div>
          </div>
        </div>
      </teleport>

      <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFile" />

      <teleport to="body">
        <div class="me-toast" :class="{ active: toastActive }">{{ toastMessage }}</div>
      </teleport>

      <!-- 图片预览遮罩层 -->
      <teleport to="body">
        <div class="mc-image-preview" :class="{ active: imagePreviewActive }" v-show="!!imagePreviewUrl" @click.self="closeImagePreview">
          <div class="mc-image-preview-content">
            <img :src="imagePreviewUrl" class="mc-image-preview-img" />
            <button type="button" class="mc-image-preview-close" @click="closeImagePreview">×</button>
          </div>
        </div>
      </teleport>

      <div class="auth-ground" aria-hidden="true">
        <div class="grass"></div>
        <div class="dirt"></div>
      </div>

      <!-- Building Snapshot Modal -->
      <div class="mc-modal-mask" :class="{ active: buildingSnapshotModalActive }" @click.self="closeBuildingSnapshotModal">
        <div class="mc-modal" :class="{ active: buildingSnapshotModalActive }">
          <h3 style="color: #000; margin: 0; font-size: 14px;">建筑快照 (已删除)</h3>
          <div class="mc-modal-scroll">
            <div v-if="buildingSnapshotData">
              <div style="margin-top: 10px;">
                <img v-if="buildingSnapshotData.cover_image" :src="buildingSnapshotData.cover_image" style="max-width: 100%; max-height: 200px; border: 2px solid #000; object-fit: contain;" />
              </div>
              <div style="margin-top: 10px; font-size: 12px; color: #000;">
                <p><strong>名称:</strong> {{ buildingSnapshotData.name }}</p>
                <p><strong>副标题:</strong> {{ buildingSnapshotData.subtitle }}</p>
                <p><strong>作者:</strong> {{ buildingSnapshotData.author }}</p>
                <p><strong>日期:</strong> {{ buildingSnapshotData.date }}</p>
                <p v-if="buildingSnapshotNotice"><strong>通知内容:</strong> {{ buildingSnapshotNotice.content }}</p>
              </div>
            </div>
            <div v-else>
              <p style="color: #000;">没有可用的建筑快照信息。</p>
            </div>
          </div>
          <div class="modal-btns">
            <button class="mc-btn" @click="closeBuildingSnapshotModal">关闭</button>
          </div>
        </div>
      </div>

      <div class="mc-modal-mask" :class="{ active: emailModalActive }" @click.self="closeEmailModal">
        <div class="mc-modal" :class="{ active: emailModalActive }">
          <h3 style="color: #000; margin: 0; font-size: 14px;">修改邮箱</h3>
          <div class="mc-modal-scroll">
            <div class="email-steps">
              <div class="step-title" :class="{ on: emailStep === 1 }">1/2 验证旧邮箱</div>
              <div class="step-title" :class="{ on: emailStep === 2 }">2/2 绑定新邮箱</div>
            </div>

            <div v-if="emailStep === 1">
              <div style="font-size: 12px; color: #000;">当前邮箱：</div>
              <div style="font-size: 12px; color: #000; font-weight: 700;">{{ profile.email || '-' }}</div>

              <div class="code-row" style="margin-top: 10px;">
                <div class="code-left">
                  <div style="font-size: 12px; color: #000; margin-bottom: 6px;">旧邮箱验证码</div>
                  <input class="mc-input" v-model.trim="oldEmailCode" placeholder="请输入旧邮箱验证码" />
                </div>
                <button class="mc-btn mc-btn-primary code-send" type="button" @click="sendOldEmailCode" :disabled="sendingOld || oldCountdown > 0 || !profile.email">
                  {{ sendingOld ? '发送中...' : oldCountdown > 0 ? `${oldCountdown}s` : '发送验证码' }}
                </button>
              </div>
            </div>

            <div v-else>
              <div class="row" style="margin-top: 6px;">
                <div style="font-size: 12px; color: #000; margin-bottom: 6px;">新邮箱</div>
                <input class="mc-input" v-model.trim="newEmail" @blur="validateNewEmail" placeholder="请输入新邮箱" />
                <div v-if="newEmailHint" class="mc-form-hint">{{ newEmailHint }}</div>
              </div>

              <div class="code-row" style="margin-top: 10px;">
                <div class="code-left">
                  <div style="font-size: 12px; color: #000; margin-bottom: 6px;">新邮箱验证码</div>
                  <input class="mc-input" v-model.trim="newEmailCode" placeholder="请输入新邮箱验证码" />
                </div>
                <button class="mc-btn mc-btn-primary code-send" type="button" @click="sendNewEmailCode" :disabled="sendingNew || newCountdown > 0 || !newEmail">
                  {{ sendingNew ? '发送中...' : newCountdown > 0 ? `${newCountdown}s` : '发送验证码' }}
                </button>
              </div>
            </div>
          </div>

          <div class="modal-btns">
            <button class="mc-btn" @click="closeEmailModal">取消</button>
            <button v-if="emailStep === 1" class="mc-btn mc-btn-primary" @click="goNextEmailStep" :disabled="!oldEmailCode">下一步</button>
            <button v-else class="mc-btn mc-btn-primary" @click="submitEmailChange" :disabled="!oldEmailCode || !newEmail || !newEmailCode">确认修改</button>
          </div>
        </div>
      </div>

      <div class="mc-modal-mask" :class="{ active: passwordModalActive }" @click.self="closePasswordModal">
        <div class="mc-modal" :class="{ active: passwordModalActive }">
          <h3 style="color: #000; margin: 0; font-size: 14px;">修改密码</h3>
          <div class="mc-modal-scroll">
            <div class="row" style="margin-top: 6px;">
              <div style="font-size: 12px; color: #000; margin-bottom: 6px;">原密码</div>
              <input class="mc-input" v-model.trim="oldPassword" type="password" placeholder="请输入原密码" />
              <div v-if="oldPasswordHint" class="mc-form-hint">{{ oldPasswordHint }}</div>
            </div>
            <div class="row" style="margin-top: 10px;">
              <div style="font-size: 12px; color: #000; margin-bottom: 6px;">新密码（至少8位）</div>
              <input class="mc-input" v-model.trim="newPassword" type="password" placeholder="请输入新密码" />
              <div v-if="newPasswordHint" class="mc-form-hint">{{ newPasswordHint }}</div>
            </div>
            <div class="row" style="margin-top: 10px;">
              <div style="font-size: 12px; color: #000; margin-bottom: 6px;">确认新密码</div>
              <input class="mc-input" v-model.trim="confirmPassword" type="password" placeholder="请再次输入新密码" />
              <div v-if="confirmPasswordHint" class="mc-form-hint">{{ confirmPasswordHint }}</div>
            </div>
          </div>
          <div class="modal-btns">
            <button class="mc-btn" @click="closePasswordModal">取消</button>
            <button class="mc-btn mc-btn-primary" @click="submitPasswordChange" :disabled="passwordSubmitting">
              {{ passwordSubmitting ? '提交中...' : '确认修改' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { mcConfirm } from '../utils/mcDialog.js'

import { API_BASE } from '../config/apiBase'

// API_BASE moved to @/config/apiBase


// 默认史蒂夫头像（你截图里那张）
const DEFAULT_STEVE = 'https://minotar.net/avatar/steve/128'

export default {
  name: 'MePage',
  setup() {
    const router = useRouter()

    let draggedIndex = null
    const handleImageDragStart = (e, index) => {
      draggedIndex = index
      e.dataTransfer.effectAllowed = 'move'
    }
    const handleImageDragOver = (e) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
    const handleImageDrop = (e, targetIndex) => {
      e.preventDefault()
      if (draggedIndex === null || draggedIndex === targetIndex) return
      const items = [...(buildForm.value.images || [])]
      const [moved] = items.splice(draggedIndex, 1)
      items.splice(targetIndex, 0, moved)
      buildForm.value.images = items
      buildForm.value.coverImage = items[0] || ''
      draggedIndex = null
    }
    const fileInput = ref(null)

    const msg = ref('')
    const error = ref('')

    const notifyError = (message) => {
      showToast(message)
    }
    const notifySuccess = (message) => {
      showToast(message)
    }

    const setMsg = (message) => {
      msg.value = message || ''
      if (message) showToast(message)
    }
    const setError = (message) => {
      error.value = message || ''
      if (message) showToast(message)
    }

    const toastActive = ref(false)
    const toastMessage = ref('')
    let toastTimer = null

    const imagePreviewUrl = ref('')
    const imagePreviewActive = ref(false)

    const openImagePreview = (url) => {
      if (!url) return
      imagePreviewUrl.value = url
      requestAnimationFrame(() => {
        imagePreviewActive.value = true
      })
    }

    const closeImagePreview = () => {
      imagePreviewActive.value = false
      setTimeout(() => {
        imagePreviewUrl.value = ''
      }, 280)
    }

    const onGlobalKeydown = (e) => {
      if (e.key === 'Escape' && imagePreviewActive.value) {
        closeImagePreview()
      }
    }

    // 通用弹窗回顶逻辑：解决手机端打开弹窗位置偏下问题
    const resetModalScroll = () => {
      nextTick(() => {
        // 1. 尝试让遮罩层滚动到顶
        const masks = document.querySelectorAll('.mc-modal-mask.active')
        masks.forEach((m) => {
          m.scrollTop = 0
        })
        // 2. 强制窗口回顶（处理某些WebView下的背景干扰）
        window.scrollTo(0, 0)
      })
    }

    const showToast = (message) => {
      if (!message) return
      toastMessage.value = String(message)
      toastActive.value = true
      if (toastTimer) clearTimeout(toastTimer)
      toastTimer = setTimeout(() => {
        toastActive.value = false
      }, 3000) // 统一 3 秒自动收回
    }

    // 兼容层：防止残留的 showNotification 调用报错或失效
    const showNotification = (message) => {
      showToast(message)
    }

    const profile = ref({})

    // Minecraft 实时时钟（用户提供的金边框 + 太阳/月亮滚动）
    const currentTimeText = ref('')
    const mcClockDialY = ref(0)
    const mcClockStatus = ref('同步世界时间中...')
    let clockTimer = null

    const getClockPeriod = (hour) => {
      const h = Number(hour)
      if (h >= 0 && h < 6) return '凌晨'
      if (h >= 6 && h < 11) return '早上'
      if (h >= 11 && h < 13) return '中午'
      if (h >= 13 && h < 18) return '下午'
      return '晚上'
    }

    const updateMinecraftClock = () => {
      const now = new Date()
      const hour = now.getHours()
      const minute = now.getMinutes()
      const second = now.getSeconds()

      currentTimeText.value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`

      // dial 高度是 outer 的 3 倍；用 0~24 映射到 0~2 的循环（0-24 => 0-2）
      // 让一天的时间推动 dial 向上滚动：0点在最上，12点在中间，24点回到最上
      const hourFloat = hour + minute / 60 + second / 3600
      const cycle = (hourFloat / 24) * 2 // 0..2
      const outer = 160
      mcClockDialY.value = -Math.round(cycle * outer)

      const period = getClockPeriod(hour)
      mcClockStatus.value = `${period} · ${period === '凌晨' ? '月亮高悬' : period === '早上' ? '旭日初升' : period === '中午' ? '日照正盛' : period === '下午' ? '余晖渐暖' : '夜色降临'}`
    }

    // 面板状态
    const isProfilePanelActive = ref(false)
    const isNotificationsPanelActive = ref(false)

    // 玩家状态（纯前端展示）
    const playerHealth = ref(100) // 0-100，对应 0-20
    const playerHunger = ref(100) // 0-100，对应 0-20
    const playerExpPercent = ref(30) // 0-100
    const playerLevel = ref(12)
    let statusTimer = null

    // 今日冒险情报（纯前端展示）
    const dailyFortune = ref({ text: '' })
    const dailyWeather = ref({ text: '' })
    const dailyMobs = ref({ text: '' })

    const hashSeed = (str) => {
      const s = String(str || '')
      let h = 2166136261
      for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i)
        h = Math.imul(h, 16777619)
      }
      return h >>> 0
    }

    const rng = (seed) => {
      // mulberry32
      let a = (seed >>> 0) || 1
      return () => {
        a |= 0
        a = (a + 0x6D2B79F5) | 0
        let t = Math.imul(a ^ (a >>> 15), 1 | a)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
      }
    }

    const pick = (arr, rand) => {
      if (!Array.isArray(arr) || arr.length === 0) return ''
      const idx = Math.floor(rand() * arr.length)
      return arr[idx]
    }

    const updateDailyIntel = () => {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = now.getMonth() + 1
      const dd = now.getDate()
      const hour = now.getHours()

      // “每天随机且当天固定”：只用日期做种子
      const dayKey = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
      const rand = rng(hashSeed(`daily-intel:${dayKey}`))

      const isNight = (hour >= 19 || hour < 6)

      const fortuneHeads = [
        '【幸运】',
        '【一般】',
        '【不宜】',
        '【小吉】',
        '【大吉】',
        '【危险】'
      ]

      const fortuneBodies = [
        '下矿手感在线，铁与煤会比平时更“主动”地出现。记得带足火把。',
        '适合清理洞穴：优先做一套完整的补给（食物、火把、方块、床）。',
        '建筑灵感爆棚：把原木、深板岩、石砖混搭，质感更“原版”。',
        '今天适合跑图：沿着河流走更安全，也更容易遇到村庄与沉船。',
        '交易运不错：多去敲敲村民的门，说不定刷出好附魔。',
        '钓鱼有惊喜：试试在清晨/傍晚甩两杆，宝藏概率更高。',
        '不宜贪心：背包别装太满，留格子给意外捡到的好东西。',
        '小心高处摔落：搭脚手架或水桶，别硬跳。',
        '小心爆炸：听到“嘶——”先拉开距离再回头。',
        '适合种田与养殖：今天整理一下农田与畜棚，效率翻倍。'
      ]

      const fortuneTips = [
        '随身携带一张床：能跳过危险夜晚，也能当作临时重生点。',
        '下界出行前先写坐标：迷路比岩浆更可怕。',
        '带一桶水：救火、攀爬、落地缓冲都靠它。',
        '背包里留 1 组圆石：补桥、封洞、挡箭都好用。',
        '夜里别直视末影人：想打再打，别惹了就跑。',
        '探索前先清空背包：战利品才是今天的主角。'
      ]

      const weatherTypes = [
        {
          name: '晴朗',
          desc: '能见度拉满，适合远行与建筑取景。',
          bonus: ['适合跑图：视野好，不容易错过结构。', '适合砍树：顺手补一波原木库存。']
        },
        {
          name: '多云',
          desc: '光线柔和，拍照更有氛围。',
          bonus: ['适合整理仓库：把箱子按“材料/工具/战利品”分区。', '适合修路：把主干道铺亮，夜里更安全。']
        },
        {
          name: '小雨',
          desc: '地表微暗，怪物刷怪点更需要留意。',
          bonus: ['适合钓鱼：雨天手感不错。', '适合采蘑菇：去阴湿处碰碰运气。']
        },
        {
          name: '雷暴',
          desc: '闪电会点燃可燃物，别把家建在干草堆旁。',
          bonus: ['不宜上高塔：避雷针/导雷结构要提前做好。', '适合室内工程：附魔、酿造、整理更稳。']
        },
        {
          name: '薄雾',
          desc: '远处发灰，路标与火把是最可靠的伙伴。',
          bonus: ['适合近距离采集：别跑太远，先把周边资源吃干净。', '适合地下工程：地上看不清就去地底挖。']
        }
      ]

      const mobScenesDay = [
        {
          title: '村庄周边',
          mobs: ['铁傀儡巡逻', '村民忙着上班', '流浪商人可能路过'],
          note: '白天交易最活跃，记得带绿宝石。'
        },
        {
          title: '花田与草地',
          mobs: ['蜜蜂出没', '兔子一闪而过', '偶遇羊群'],
          note: '剪点羊毛，留给床与地毯。'
        },
        {
          title: '沼泽边缘',
          mobs: ['史莱姆跳跳糖', '青蛙呱呱叫'],
          note: '想要粘液球的话，今晚也可以来蹲点。'
        },
        {
          title: '海岸线',
          mobs: ['海豚指路', '溺尸偶尔冒头'],
          note: '看到沉船就别客气，战利品在等你。'
        }
      ]

      const mobScenesNight = [
        {
          title: '林地与平原',
          mobs: ['僵尸游荡', '骷髅远程点名', '苦力怕悄悄靠近'],
          note: '带盾 + 保持亮度，听声辨位是王道。'
        },
        {
          title: '洞穴入口',
          mobs: ['蜘蛛贴墙', '骷髅卡角落', '蝙蝠乱飞'],
          note: '先封口插火把，再慢慢推进。'
        },
        {
          title: '屋檐与树顶',
          mobs: ['蜘蛛可能从上方跳脸', '幻翼盯上熬夜的你'],
          note: '三天不睡就会来烦你，赶紧回床上。'
        },
        {
          title: '荒野道路',
          mobs: ['流浪者在雪地潜伏', '女巫躲在黑暗里'],
          note: '别恋战，先把补给和战利品带回家。'
        }
      ]

      const head = pick(fortuneHeads, rand)
      const body = pick(fortuneBodies, rand)
      const tip = pick(fortuneTips, rand)
      dailyFortune.value = { text: `${head}${body}（建议：${tip}）` }

      const weather = pick(weatherTypes, rand)
      const weatherBonus = pick(weather.bonus, rand)
      dailyWeather.value = { text: `${weather.name}：${weather.desc}｜${weatherBonus}` }

      const scene = pick(isNight ? mobScenesNight : mobScenesDay, rand)
      const mobsText = scene.mobs.join('、')
      dailyMobs.value = { text: `${scene.title}：${mobsText}。${scene.note}` }
    }

    const getNoticeIcon = (n) => {
      if (!n) return '📜'
      const text = String(n.content || '')
      if (text.includes('重要') || text.includes('紧急')) return '📌'
      if (text.includes('活动') || text.includes('集合')) return '🎉'
      if (text.includes('建') || text.includes('施工')) return '🧱'
      return '📜'
    }

    const getBuildingIcon = (b) => {
      if (!b) return '🏠'
      const name = String(b.name || '')
      if (name.includes('塔') || name.toLowerCase().includes('tower')) return '🗼'
      if (name.includes('桥')) return '🌉'
      if (name.includes('港') || name.includes('码头')) return '⚓'
      if (name.includes('城') || name.includes('堡')) return '🏰'
      return '🏠'
    }

    const tickPlayerStatus = () => {
      // 轻微呼吸/波动动画：不与业务数据耦合
      const t = Date.now() / 1000
      const wobble = (amp, speed, offset = 0) => amp * Math.sin(t * speed + offset)
      const baseHealth = 92
      const baseHunger = 88

      playerHealth.value = Math.max(0, Math.min(100, Math.round(baseHealth + wobble(6, 0.6, 0.3))))
      playerHunger.value = Math.max(0, Math.min(100, Math.round(baseHunger + wobble(7, 0.45, 1.2))))

      // 经验条：当天进度（0-100）
      playerExpPercent.value = Math.max(0, Math.min(100, Math.round(getTodayProgressPercent())))

      // 等级：按注册天数递增，注册当天 Lv.1
      playerLevel.value = Math.max(1, getLevelByRegisteredAt())
    }

    const getRegisteredAt = () => {
      // 优先使用后端返回的 createdAt（真实注册时间）
      try {
        const p = profile.value || {}
        if (p.createdAt) return new Date(p.createdAt)
        if (p.registeredAt) return new Date(p.registeredAt)
      } catch (e) {
        // ignore
      }

      // 兜底：若没有 createdAt，则以首次进入 /me 的时间作为“注册时间”
      try {
        const key = 'user_registered_at'
        const raw = localStorage.getItem(key)
        if (raw) return new Date(raw)
        const now = new Date().toISOString()
        localStorage.setItem(key, now)
        return new Date(now)
      } catch (e) {
        return new Date()
      }
    }

    const startOfDay = (d) => {
      const x = new Date(d)
      x.setHours(0, 0, 0, 0)
      return x
    }

    const getLevelByRegisteredAt = () => {
      const reg = getRegisteredAt()
      const now = new Date()
      const diff = startOfDay(now).getTime() - startOfDay(reg).getTime()
      const days = Math.floor(diff / (24 * 60 * 60 * 1000))
      return 1 + Math.max(0, days)
    }

    const getTodayProgressPercent = () => {
      const now = new Date()
      const start = startOfDay(now)
      const ms = now.getTime() - start.getTime()
      const pct = (ms / (24 * 60 * 60 * 1000)) * 100
      return pct
    }


    // ========== 通知中心 ==========
    const notifications = ref([])
    const unreadCount = ref(0)

    const goBuilding = (id) => {
      if (!id) return
      router.push({ path: `/building/${id}`, query: { from: 'me' } })
    }

    const parseBuildingSnapshot = (notice) => {
      if (!notice) return null
      if (typeof notice === 'object') return notice
      try {
        return JSON.parse(notice)
      } catch (e) {
        return null
      }
    }

    // --- 自定义像素风日历逻辑开始 ---
    const showDatePicker = ref(false)
    const currentYear = ref(new Date().getFullYear())
    const currentMonth = ref(new Date().getMonth())

    const toggleDatePicker = () => {
      showDatePicker.value = !showDatePicker.value
      if (buildForm.value.dateValue) {
        const d = new Date(buildForm.value.dateValue)
        if (!isNaN(d.getTime())) {
          currentYear.value = d.getFullYear()
          currentMonth.value = d.getMonth()
        }
      }
    }

    const prevYear = () => {
      currentYear.value--
    }

    const nextYear = () => {
      currentYear.value++
    }

    const prevMonth = () => {
      if (currentMonth.value === 0) {
        currentYear.value--
        currentMonth.value = 11
      } else {
        currentMonth.value--
      }
    }

    const nextMonth = () => {
      if (currentMonth.value === 11) {
        currentYear.value++
        currentMonth.value = 0
      } else {
        currentMonth.value++
      }
    }

    const daysInMonth = computed(() => {
      return new Date(currentYear.value, currentMonth.value + 1, 0).getDate()
    })

    const firstDayOfMonth = computed(() => {
      return new Date(currentYear.value, currentMonth.value, 1).getDay()
    })

    const isSelected = (day) => {
      if (!buildForm.value.dateValue) return false
      const d = new Date(buildForm.value.dateValue)
      return d.getFullYear() === currentYear.value && 
             d.getMonth() === currentMonth.value && 
             d.getDate() === day
    }

    const selectDate = (day) => {
      const y = currentYear.value
      const m = String(currentMonth.value + 1).padStart(2, '0')
      const d = String(day).padStart(2, '0')
      buildForm.value.dateValue = `${y}-${m}-${d}`
      buildForm.value.date = 'date'
      showDatePicker.value = false
    }

    const selectToday = () => {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      buildForm.value.dateValue = `${y}-${m}-${d}`
      buildForm.value.date = 'date'
      showDatePicker.value = false
    }

    const clearDate = () => {
      buildForm.value.dateValue = ''
      buildForm.value.date = ''
      showDatePicker.value = false
    }
    // --- 自定义像素风日历逻辑结束 ---

    const buildingSnapshotModalActive = ref(false)
    const buildingSnapshotData = ref(null)
    const buildingSnapshotNotice = ref(null)

    const openBuildingSnapshotModal = (notice) => {
      buildingSnapshotNotice.value = notice || null
      buildingSnapshotData.value = parseBuildingSnapshot(notice && notice.buildingSnapshot ? notice.buildingSnapshot : null)
      buildingSnapshotModalActive.value = true
    }

    const closeBuildingSnapshotModal = () => {
      buildingSnapshotModalActive.value = false
      buildingSnapshotData.value = null
      buildingSnapshotNotice.value = null
    }

    const handleBuildingNotificationClick = async (notice) => {
      if (!notice) return

      const snapshot = parseBuildingSnapshot(notice.buildingSnapshot)
      if (snapshot) {
        router.push(`/building-snapshot/${notice.id}`)
        return
      }

      if (!notice.relatedBuildingId) return
      goBuilding(notice.relatedBuildingId)
    }

    const loadNotifications = async () => {
      const token = localStorage.getItem('user_token')
      if (!token) return
      try {
        const res = await fetch(`${API_BASE}/user/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data && data.code === 200) {
          notifications.value = data.data || []
          unreadCount.value = Number(data.unread || 0)
        }
      } catch (e) {
        // ignore
      }
    }

    const markRead = async (id) => {
      const token = localStorage.getItem('user_token')
      if (!token) return
      try {
        const res = await fetch(`${API_BASE}/user/notifications/${id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data && data.code === 200) {
          await loadNotifications()
        }
      } catch (e) {
        // ignore
      }
    }

    // ========== 我的建筑 ==========
    const myBuildings = ref([])
    const buildModalActive = ref(false)
    const buildSubmitting = ref(false)
    const editingMyBuilding = ref(null)

    const buildForm = ref({
      name: '',
      subtitle: '',
      date: '',
      dateValue: '',
      author: '',
      coverImage: '',
      images: [],
      descriptionJson: ''
    })

    

    const buildImagesPreviews = ref([])
    const originalBuildForm = ref(null)


    const openBuildModal = () => {
      msg.value = ''
      error.value = ''
      editingMyBuilding.value = null
      buildForm.value = {
        name: '',
        subtitle: '',
        date: '',
        dateValue: '',
        author: '',
        coverImage: '',
        images: [],
        descriptionJson: ''
      }
      originalBuildForm.value = JSON.parse(JSON.stringify(buildForm.value))
      buildImagesPreviews.value = []
      buildModalActive.value = true
      resetModalScroll()
      resetModalScroll()
      requestAnimationFrame(() => {
        const masks = document.querySelectorAll('.mc-modal-mask.active')
        masks.forEach((m) => {
          try {
            m.scrollTop = 0
          } catch (e) {
            // ignore
          }
        })
      })
    }

    const openEditBuildModal = (b) => {
      msg.value = ''
      error.value = ''
      if (!b) return

      const buildingId = b.buildingId || b.id
      editingMyBuilding.value = { ...b, buildingId }

      let imagesArr = []
      let descLines = ''
      try {
        if (b.images) {
          let parsedImages = b.images
          if (typeof b.images === 'string') {
            try {
              parsedImages = JSON.parse(b.images)
            } catch (e) {
              if (b.images.includes(',')) {
                parsedImages = b.images.split(',').map(u => u.trim()).filter(Boolean)
              } else {
                parsedImages = [b.images]
              }
            }
          }
          if (Array.isArray(parsedImages)) {
            imagesArr = parsedImages.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim())
          }
        }
        if (b.description) {
          let parsedDesc = b.description
          if (typeof b.description === 'string') {
            try {
              parsedDesc = JSON.parse(b.description)
            } catch (e) {
              parsedDesc = b.description.split('\n').map(l => l.trim()).filter(Boolean)
            }
          }
          if (Array.isArray(parsedDesc)) {
            descLines = parsedDesc.map((x) => String(x)).join('\n')
          } else if (typeof parsedDesc === 'string') {
            descLines = parsedDesc
          }
        }
      } catch (e) {
        console.error('openEditBuildModal parse error', e)
      }

      buildForm.value = {
        name: b.name || '',
        subtitle: b.subtitle || '',
        date: b.date === 'unknown' ? 'unknown' : (b.date ? 'date' : ''),
        dateValue: b.date === 'unknown' ? '' : (b.date || ''),
        author: b.author || '',
        coverImage: b.coverImage || '',
        images: imagesArr,
        descriptionJson: descLines
      }

      originalBuildForm.value = JSON.parse(JSON.stringify(buildForm.value))
      buildImagesPreviews.value = []
      buildModalActive.value = true
    }

    const closeBuildModal = () => {
      buildModalActive.value = false
      editingMyBuilding.value = null
      buildImagesPreviews.value = []

      // 清理上传队列并释放 object URL（避免多图预览导致内存上涨、卡顿）
      if (Array.isArray(buildUploadQueue.value)) {
        buildUploadQueue.value.forEach((item) => {
          try {
            if (item && item.localUrl) URL.revokeObjectURL(item.localUrl)
          } catch (e) {
            // ignore
          }
        })
        buildUploadQueue.value = []
      }
    }

    const loadMyBuildings = async () => {
      const token = localStorage.getItem('user_token')
      if (!token) return
      try {
        const res = await fetch(`${API_BASE}/user/buildings/mine`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data && data.code === 200 && Array.isArray(data.data)) {
          myBuildings.value = data.data.filter(b => b.isDelete !== 1 && b.is_delete !== 1)
        }
      } catch (e) {
        // ignore
      }
    }


    // 建筑图片上传队列（限制并发，避免多图同时上传导致卡顿/错位）
    // 队列项结构：{ id, file, localUrl, status: 'pending'|'uploading'|'success'|'error', remoteUrl, error, retry }
    const buildUploadQueue = ref([])
    const MAX_BUILD_UPLOAD_CONCURRENCY = 2
    const buildUploadingCount = ref(0)

    const enqueueBuildFiles = (files) => {
      if (!Array.isArray(files) || files.length === 0) return
      files.forEach((file) => {
        const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
        const localUrl = URL.createObjectURL(file)
        buildUploadQueue.value.push({
          id,
          file,
          localUrl,
          status: 'pending',
          remoteUrl: '',
          error: '',
          retry: 0
        })
      })
      processBuildUploadQueue()
    }

    const handleBuildImagesChange = (e) => {
      const files = Array.from((e.target.files || []))
      if (files.length === 0) return

      const currentCount = Array.isArray(buildForm.value.images) ? buildForm.value.images.length : 0
      const uploadingCount = Array.isArray(buildUploadQueue.value) ? buildUploadQueue.value.length : 0
      const totalAfterSelect = currentCount + uploadingCount + files.length

      if (totalAfterSelect > 10) {
        showNotification('建筑图片最多只能上传 10 张', 'error', '警告')
        return
      }

      enqueueBuildFiles(files)
      try {
        e.target.value = ''
      } catch (err) {
        // ignore
      }
    }

    const uploadOneBuildQueueItem = async (item) => {
      item.status = 'uploading'
      item.error = ''
      buildUploadingCount.value += 1
      try {
        const formData = new FormData()
        formData.append('file', item.file)
        const res = await fetch(`${API_BASE}/upload/image`, { method: 'POST', body: formData })
        const data = await res.json()
        if (data && data.code === 200 && data.url) {
          item.status = 'success'
          item.remoteUrl = data.url

          if (!Array.isArray(buildForm.value.images)) buildForm.value.images = []

          // 上限保护：并发上传过程中也避免超过 10 张
          if (buildForm.value.images.length >= 10) {
            item.status = 'error'
            item.error = '建筑图片最多只能上传 10 张'
          } else {
            buildForm.value.images.push(data.url)

            // 第一张图片自动作为封面（方案A：已有封面则不覆盖）
            if (!buildForm.value.coverImage) {
              buildForm.value.coverImage = data.url
            }

            showNotification('图片上传成功', 'success', '成功')
          }

          // 成功项自动从队列移除（释放 objectURL，避免队列越积越多导致卡顿）
          setTimeout(() => {
            const idx = buildUploadQueue.value.findIndex((x) => x && x.id === item.id)
            if (idx >= 0) {
              const it = buildUploadQueue.value[idx]
              try {
                if (it && it.localUrl) URL.revokeObjectURL(it.localUrl)
              } catch (e) {
                // ignore
              }
              buildUploadQueue.value.splice(idx, 1)
            }
          }, 800)
        } else {
          item.status = 'error'
          item.error = (data && data.message) ? data.message : '图片上传失败'
        }
      } catch (e) {
        item.status = 'error'
        item.error = '图片上传失败'
      } finally {
        buildUploadingCount.value = Math.max(0, buildUploadingCount.value - 1)
      }
    }

    const processBuildUploadQueue = () => {
      while (buildUploadingCount.value < MAX_BUILD_UPLOAD_CONCURRENCY) {
        const next = buildUploadQueue.value.find((x) => x.status === 'pending')
        if (!next) break
        uploadOneBuildQueueItem(next).finally(() => {
          processBuildUploadQueue()
        })
      }
    }

    const retryBuildUpload = (id) => {
      const item = buildUploadQueue.value.find((x) => x.id === id)
      if (!item) return
      if (item.retry >= 2) {
        showNotification('重试次数过多', 'error', '警告')
        return
      }
      item.retry += 1
      item.status = 'pending'
      item.error = ''
      processBuildUploadQueue()
    }

    const removeBuildUploadItem = (id) => {
      const idx = buildUploadQueue.value.findIndex((x) => x && x.id === id)
      if (idx < 0) return
      const it = buildUploadQueue.value[idx]
      try {
        if (it && it.localUrl) URL.revokeObjectURL(it.localUrl)
      } catch (e) {
        // ignore
      }
      buildUploadQueue.value.splice(idx, 1)
    }

    // 兼容旧模板调用（已不再使用）
    const uploadBuildImage = async () => {}

    const removeBuildImage = (index) => {
      if (!Array.isArray(buildForm.value.images)) return
      const removed = buildForm.value.images[index]
      buildForm.value.images.splice(index, 1)

      if (buildForm.value.coverImage === removed) {
        buildForm.value.coverImage = buildForm.value.images[0] || ''
      }
    }

    const validateBuildingName = () => {
      const BUILD_NAME_MAX = 15
      const name = String(buildForm.value.name || '')
      if (name.length > BUILD_NAME_MAX) {
        buildForm.value.name = name.substring(0, BUILD_NAME_MAX)
      }
    }

    const validateBuildingSubtitle = () => {
      const BUILD_SUBTITLE_MAX = 20
      const subtitle = String(buildForm.value.subtitle || '')
      if (subtitle.length > BUILD_SUBTITLE_MAX) {
        buildForm.value.subtitle = subtitle.substring(0, BUILD_SUBTITLE_MAX)
      }
    }

    const submitBuild = async () => {
      msg.value = ''
      error.value = ''

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }

      const BUILD_NAME_MAX = 15
      const BUILD_SUBTITLE_MAX = 20

      // 1. 建筑名称必填
      if (!buildForm.value.name || !String(buildForm.value.name).trim()) {
        showNotification('请输入建筑名称', 'error', '警告')
        return
      }
      if (String(buildForm.value.name).length > BUILD_NAME_MAX) {
        showNotification(`建筑名称最多 ${BUILD_NAME_MAX} 个字`, 'error', '警告')
        return
      }

      // 2. 副标题必填
      if (!buildForm.value.subtitle || !String(buildForm.value.subtitle).trim()) {
        showNotification('请输入副标题', 'error', '警告')
        return
      }
      if (String(buildForm.value.subtitle).length > BUILD_SUBTITLE_MAX) {
        showNotification(`副标题最多 ${BUILD_SUBTITLE_MAX} 个字`, 'error', '警告')
        return
      }

      // 3. 建筑师必填
      if (!buildForm.value.author || !String(buildForm.value.author).trim()) {
        showNotification('请输入建筑师', 'error', '警告')
        return
      }

      // 4. 日期必填且合法，且范围必须在 [2024-01-01, 今天]，支持“未知”
      if (!buildForm.value.date || !String(buildForm.value.date).trim()) {
        setError('请选择建成时间')
        return
      }
      // 选择“未知”直接通过日期校验
      if (buildForm.value.date !== 'unknown') {
        if (buildForm.value.date !== 'date') {
          setError('请选择建成时间')
          return
        }
        if (!buildForm.value.dateValue || !String(buildForm.value.dateValue).trim()) {
          setError('请选择建成时间')
          return
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(buildForm.value.dateValue)) {
          setError('请输入完整日期 (YYYY-MM-DD)')
          return
        }
        const inputDate = new Date(buildForm.value.dateValue)
        const today = new Date()
        const minDate = new Date('2024-01-01')
        if (inputDate < minDate) {
          setError('你填的时间有误！请仔细检查~')
          return
        }
        if (inputDate > today) {
          setError('日期不能大于当前时间')
          return
        }
      }

      // 5. 描述必填（编辑时不做字数限制）
      const descriptionLines = String(buildForm.value.descriptionJson || '')
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean)
      const totalDescChars = descriptionLines.join('').length
      if (descriptionLines.length === 0 || totalDescChars === 0) {
        showNotification('请输入建筑描述', 'error', '警告')
        return
      }
      const isEdit = !!editingMyBuilding.value
      if (!isEdit) {
        if (totalDescChars < 100) {
          showNotification('描述内容至少需要 100 字', 'error', '警告')
          return
        }
        if (totalDescChars > 5000) {
          showNotification('描述内容不能超过 5000 字', 'error', '警告')
          return
        }
      }

      // 6. 图片数量校验（编辑时兼容老数据）
      const images = buildForm.value.images || []
      if (!Array.isArray(images)) {
        showNotification('请上传建筑图片', 'error', '警告')
        return
      }
      if (!isEdit) {
        if (images.length < 3) {
          showNotification('建筑图片至少需要 3 张', 'error', '警告')
          return
        }
        if (images.length > 10) {
          showNotification('建筑图片最多只能上传 10 张', 'error', '警告')
          return
        }
      }

      // 7. 封面图必填（取第一张）
      if (!buildForm.value.coverImage || !String(buildForm.value.coverImage).trim()) {
        if (images.length > 0) {
          buildForm.value.coverImage = images[0]
        } else {
          setError('请上传建筑图片')
          return
        }
      }

      if (isEdit && JSON.stringify(buildForm.value) === JSON.stringify(originalBuildForm.value)) {
        closeBuildModal()
        return
      }

      buildSubmitting.value = true
      try {
        const description = descriptionLines

        const payload = {
          name: String(buildForm.value.name).trim(),
          subtitle: String(buildForm.value.subtitle).trim(),
          date: buildForm.value.date === 'unknown' ? 'unknown' : String(buildForm.value.dateValue).trim(),
          author: String(buildForm.value.author).trim(),
          coverImage: String(buildForm.value.coverImage).trim(),
          images: JSON.stringify(images),
          description: JSON.stringify(description),
          sortOrder: 0
        }

        const isEdit = !!editingMyBuilding.value
        const buildingId = isEdit ? (editingMyBuilding.value.buildingId || editingMyBuilding.value.id) : null
        const url = isEdit ? `${API_BASE}/user/buildings/${buildingId}` : `${API_BASE}/user/buildings`
        const method = isEdit ? 'PUT' : 'POST'

        const res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        })
        const data = await res.json()
        if (data && data.code === 200) {
          if (isEdit) {
            setMsg(data.message || '建筑更新成功')
          } else {
            setMsg(data.message || '建筑创建成功')
          }

          closeBuildModal()
          await loadMyBuildings()
          await loadNotifications()
        } else {
          setError((data && data.message) || (isEdit ? '更新失败' : '发布失败'))
        }
      } catch (e) {
        console.error('submitBuild failed', e)
        showNotification(editingMyBuilding.value ? '更新失败' : '发布失败', 'error', '错误')
        setError(editingMyBuilding.value ? '更新失败' : '发布失败')
      } finally {
        buildSubmitting.value = false
      }
    }

    const deleteMyBuilding = async (buildingId) => {
      msg.value = ''
      error.value = ''
      if (!buildingId) return
      if (!(await mcConfirm('确定要删除这个建筑吗？删除后将从首页移除。'))) return

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }

      try {
        const res = await fetch(`${API_BASE}/user/buildings/${buildingId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || '删除成功')
          await loadMyBuildings()
          await loadNotifications()
        } else {
          setError((data && data.message) || '删除失败')
        }
      } catch (e) {
        setError('删除失败')
      }
    }

    // ========== 我的公告 ==========
    const myNotices = ref([])
    const noticeDraft = ref('')
    const noticeDraftHint = ref('')
    const editingNoticeId = ref(null)
    const noticeSubmitting = ref(false)
    const originalNoticeDraft = ref('')
    const noticeModalActive = ref(false)

    const remainingNoticeCount = computed(() => {
      const left = 5 - (Array.isArray(myNotices.value) ? myNotices.value.length : 0)
      return left < 0 ? 0 : left
    })

    const formatDateTime = (iso) => {
      if (!iso) return '-'
      try {
        return String(iso).replace('T', ' ').substring(0, 16)
      } catch (e) {
        return String(iso)
      }
    }

    const formatRemaining = (expireAt) => {
      if (!expireAt) return '-'
      try {
        const now = new Date(noticeNowTick.value)
        const exp = new Date(expireAt)
        if (exp <= now) {
          return '已过期'
        }
        const diff = exp - now
        const d = Math.floor(diff / (1000 * 60 * 60 * 24))
        const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const s = Math.floor((diff % (1000 * 60)) / 1000)
        return `${d}天${h}时${m}分${s}秒`
      } catch (e) {
        return '-'
      }
    }

    const remainingBadgeClass = (expireAt) => {
      if (!expireAt) return 'me-badge-neutral'
      try {
        const now = new Date()
        const exp = new Date(expireAt)
        const diff = exp - now
        if (diff <= 0) return 'me-badge-danger'
        if (diff <= 60 * 60 * 1000) return 'me-badge-danger'
        if (diff <= 24 * 60 * 60 * 1000) return 'me-badge-warn'
        return 'me-badge-ok'
      } catch (e) {
        return 'me-badge-neutral'
      }
    }

    const validateNoticeDraft = () => {
      const v = (noticeDraft.value || '').trim()
      if (!v) {
        showNotification('请输入公告内容', 'error', '警告')
        noticeDraftHint.value = ''
        return false
      }
      if (v.length > 300) {
        showNotification('公告内容最多300字', 'error', '警告')
        noticeDraftHint.value = ''
        return false
      }
      noticeDraftHint.value = ''
      return true
    }

    const loadMyNotices = async () => {
      const token = localStorage.getItem('user_token')
      if (!token) return
      try {
        const res = await fetch(`${API_BASE}/user/notices`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data && data.code === 200) {
          myNotices.value = data.data || []
        }
      } catch (e) {
        // ignore
      }
    }

    const openNoticeModalForCreate = () => {
      msg.value = ''
      error.value = ''
      editingNoticeId.value = null
      noticeDraft.value = ''
      originalNoticeDraft.value = ''
      noticeDraftHint.value = ''
      noticeModalActive.value = true
      resetModalScroll()
    }

    const openNoticeModalForEdit = (n) => {
      if (!n) return
      msg.value = ''
      error.value = ''
      editingNoticeId.value = n.id
      noticeDraft.value = n.content || ''
      originalNoticeDraft.value = n.content || ''
      noticeDraftHint.value = ''
      noticeModalActive.value = true
      resetModalScroll()
    }

    const closeNoticeModal = () => {
      noticeModalActive.value = false
      editingNoticeId.value = null
      noticeDraft.value = ''
      originalNoticeDraft.value = ''
      noticeDraftHint.value = ''
    }

    const submitNotice = async () => {
      msg.value = ''
      error.value = ''
      if (!validateNoticeDraft()) return

      if (!editingNoticeId.value && remainingNoticeCount.value <= 0) {
        setError('每个用户最多只能发布5个公告')
        return
      }

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }

      const isEdit = !!editingNoticeId.value
      if (isEdit && String(noticeDraft.value || '') === String(originalNoticeDraft.value || '')) {
        closeNoticeModal()
        return
      }

      noticeSubmitting.value = true
      try {
        const url = isEdit ? `${API_BASE}/user/notices/${editingNoticeId.value}` : `${API_BASE}/user/notices`
        const method = isEdit ? 'PUT' : 'POST'
        const res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ content: noticeDraft.value })
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || (isEdit ? '更新成功' : '发布成功'))
          closeNoticeModal()
          await loadMyNotices()
        } else {
          setError((data && data.message) || '操作失败')
        }
      } catch (e) {
        setError('操作失败')
      } finally {
        noticeSubmitting.value = false
      }
    }

    const editNotice = (n) => {
      openNoticeModalForEdit(n)
    }

    const cancelEditNotice = () => {
      closeNoticeModal()
    }

    const deleteNotice = async (id) => {
      msg.value = ''
      error.value = ''
      if (!(await mcConfirm('确定要删除这条公告吗？'))) return

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }

      try {
        const res = await fetch(`${API_BASE}/user/notices/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || '删除成功')
          if (editingNoticeId.value === id) {
            cancelEditNotice()
          }
          await loadMyNotices()
        } else {
          setError((data && data.message) || '删除失败')
        }
      } catch (e) {
        setError('删除失败')
      }
    }

    const emailModalActive = ref(false)
    const emailStep = ref(1)

    const oldEmailCode = ref('')
    const newEmail = ref('')
    const newEmailCode = ref('')
    const newEmailHint = ref('')

    const sendingOld = ref(false)
    const sendingNew = ref(false)
    const oldCountdown = ref(0)
    const newCountdown = ref(0)
    let oldTimer = null
    let newTimer = null

    const isEmail = (v) => /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/.test(v || '')

    const loadProfile = async () => {
      try {
        const raw = localStorage.getItem('user_profile')
        profile.value = raw ? JSON.parse(raw) : {}
        // 如果本地没有 createdAt，尝试从后端拉取一次
        if (!profile.value.createdAt) {
          const token = localStorage.getItem('user_token')
          if (token) {
            try {
              const res = await fetch(`${API_BASE}/user/profile`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              const data = await res.json()
              if (data && data.code === 200 && data.data && data.data.createdAt) {
                profile.value.createdAt = data.data.createdAt
                localStorage.setItem('user_profile', JSON.stringify(profile.value))
              }
            } catch (e) {
              // ignore
            }
          }
        }
      } catch (e) {
        profile.value = {}
      }
    }

    const avatarSrc = computed(() => {
      const u = profile.value && profile.value.avatarUrl ? String(profile.value.avatarUrl).trim() : ''
      return u ? u : DEFAULT_STEVE
    })

    const openEmailModal = () => {
      msg.value = ''
      error.value = ''
      emailModalActive.value = true
      emailStep.value = 1
      oldEmailCode.value = ''
      newEmail.value = ''
      newEmailCode.value = ''
      newEmailHint.value = ''
      resetModalScroll()
    }

    const closeEmailModal = () => {
      emailModalActive.value = false
      emailStep.value = 1
    }

    const passwordModalActive = ref(false)
    const oldPassword = ref('')
    const newPassword = ref('')
    const confirmPassword = ref('')
    const oldPasswordHint = ref('')
    const newPasswordHint = ref('')
    const confirmPasswordHint = ref('')
    const passwordSubmitting = ref(false)

    const openPasswordModal = () => {
      msg.value = ''
      error.value = ''
      passwordModalActive.value = true
      oldPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''
      oldPasswordHint.value = ''
      newPasswordHint.value = ''
      confirmPasswordHint.value = ''
      resetModalScroll()
    }

    const closePasswordModal = () => {
      passwordModalActive.value = false
    }

    const validatePasswordForm = () => {
      oldPasswordHint.value = ''
      newPasswordHint.value = ''
      confirmPasswordHint.value = ''

      if (!oldPassword.value) {
        oldPasswordHint.value = '请输入原密码'
        return false
      }
      if (!newPassword.value) {
        newPasswordHint.value = '请输入新密码'
        return false
      }
      if (newPassword.value.length < 8) {
        newPasswordHint.value = '密码至少8位'
        return false
      }
      if (oldPassword.value === newPassword.value) {
        newPasswordHint.value = '新密码不能与原密码相同'
        return false
      }
      if (!confirmPassword.value) {
        confirmPasswordHint.value = '请再次输入新密码'
        return false
      }
      if (confirmPassword.value !== newPassword.value) {
        confirmPasswordHint.value = '两次输入的密码不一致'
        return false
      }
      return true
    }

    const submitPasswordChange = async () => {
      msg.value = ''
      error.value = ''
      if (!validatePasswordForm()) return

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }

      passwordSubmitting.value = true
      try {
        const res = await fetch(`${API_BASE}/user/password/change`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            oldPassword: oldPassword.value,
            newPassword: newPassword.value,
            confirmPassword: confirmPassword.value
          })
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || '密码修改成功')
          closePasswordModal()
        } else {
          setError((data && data.message) || '密码修改失败')
        }
      } catch (e) {
        setError('密码修改失败')
      } finally {
        passwordSubmitting.value = false
      }
    }

    const validateNewEmail = () => {
      if (!newEmail.value) {
        showNotification('请输入新邮箱', 'error', '警告')
        newEmailHint.value = '请输入新邮箱'
        return false
      }
      if (!isEmail(newEmail.value)) {
        showNotification('邮箱格式不正确', 'error', '警告')
        newEmailHint.value = '邮箱格式不正确'
        return false
      }
      newEmailHint.value = ''
      return true
    }

    const checkEmailAvailable = async () => {
      if (!validateNewEmail()) return false

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return false
      }

      try {
        const res = await fetch(`${API_BASE}/user/email/change/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ email: newEmail.value })
        })
        const data = await res.json()
        if (data && data.code === 200) return true
        if (data && data.code === 409) {
          showNotification('该邮箱已被注册', 'error', '警告')
          newEmailHint.value = '该邮箱已被注册'
          return false
        }
        showNotification((data && data.message) || '邮箱校验失败', 'error', '警告')
        return false
      } catch (e) {
        showNotification('邮箱校验失败', 'error', '警告')
        return false
      }
    }

    const startOldCountdown = () => {
      oldCountdown.value = 60
      if (oldTimer) clearInterval(oldTimer)
      oldTimer = setInterval(() => {
        oldCountdown.value -= 1
        if (oldCountdown.value <= 0) {
          clearInterval(oldTimer)
          oldTimer = null
        }
      }, 1000)
    }

    const startNewCountdown = () => {
      newCountdown.value = 60
      if (newTimer) clearInterval(newTimer)
      newTimer = setInterval(() => {
        newCountdown.value -= 1
        if (newCountdown.value <= 0) {
          clearInterval(newTimer)
          newTimer = null
        }
      }, 1000)
    }

    const sendOldEmailCode = async () => {
      error.value = ''
      msg.value = ''

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }
      if (!profile.value || !profile.value.email) {
        setError('当前账号未绑定邮箱')
        return
      }
      if (oldCountdown.value > 0) return

      sendingOld.value = true
      try {
        const res = await fetch(`${API_BASE}/user-auth/sendEmailCode`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: profile.value.email, scene: 'change_old' })
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || '验证码已发送')
          startOldCountdown()
        } else {
          setError((data && data.message) || '发送失败')
        }
      } catch (e) {
        setError('发送失败')
      } finally {
        sendingOld.value = false
      }
    }

    const sendNewEmailCode = async () => {
      error.value = ''
      msg.value = ''

      if (!(await checkEmailAvailable())) return
      if (newCountdown.value > 0) return

      sendingNew.value = true
      try {
        const res = await fetch(`${API_BASE}/user-auth/sendEmailCode`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newEmail.value, scene: 'change_new' })
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || '验证码已发送')
          startNewCountdown()
        } else {
          setError((data && data.message) || '发送失败')
        }
      } catch (e) {
        setError('发送失败')
      } finally {
        sendingNew.value = false
      }
    }

    const goNextEmailStep = () => {
      error.value = ''
      msg.value = ''
      if (!oldEmailCode.value) {
        setError('请输入旧邮箱验证码')
        return
      }
      emailStep.value = 2
    }

    const submitEmailChange = async () => {
      error.value = ''
      msg.value = ''

      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }
      if (!oldEmailCode.value) {
        setError('请输入旧邮箱验证码')
        return
      }
      if (!(await checkEmailAvailable())) return
      if (!newEmailCode.value) {
        setError('请输入新邮箱验证码')
        return
      }

      try {
        const res = await fetch(`${API_BASE}/user/email/change`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            oldEmailCode: oldEmailCode.value,
            newEmail: newEmail.value,
            newEmailCode: newEmailCode.value
          })
        })
        const data = await res.json()
        if (data && data.code === 200) {
          setMsg(data.message || '邮箱修改成功')

          const nextProfile = {
            ...(profile.value || {}),
            email: data.data && data.data.email ? data.data.email : newEmail.value
          }
          profile.value = nextProfile
          localStorage.setItem('user_profile', JSON.stringify(nextProfile))

          closeEmailModal()
        } else {
          setError((data && data.message) || '邮箱修改失败')
        }
      } catch (e) {
        setError('邮箱修改失败')
      }
    }

    const onAvatarClick = () => {
      msg.value = ''
      error.value = ''
      if (fileInput.value) fileInput.value.click()
    }

    const handleFile = async (e) => {
      msg.value = ''
      error.value = ''
      const file = e.target.files && e.target.files[0]
      if (!file) return

      const token = localStorage.getItem('user_token')
      if (!token) {
        setError('未登录')
        e.target.value = ''
        router.replace('/login')
        return
      }

      try {
        const formData = new FormData()
        formData.append('file', file)
        const uploadRes = await fetch(`${API_BASE}/upload/image`, {
          method: 'POST',
          body: formData
        })
        const uploadData = await uploadRes.json()
        if (!uploadData || uploadData.code !== 200 || !uploadData.url) {
          error.value = (uploadData && uploadData.message) || '头像上传失败'
          e.target.value = ''
          return
        }

        const avatarUrl = uploadData.url

        const updateRes = await fetch(`${API_BASE}/user/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ avatarUrl })
        })
        const updateData = await updateRes.json()
        if (!updateData || updateData.code !== 200) {
          error.value = (updateData && updateData.message) || '更新头像失败'
          e.target.value = ''
          return
        }

        const data = updateData.data || {}
        const nextProfile = {
          ...(profile.value || {}),
          avatarUrl: data.avatarUrl || avatarUrl
        }

        profile.value = nextProfile
        localStorage.setItem('user_profile', JSON.stringify(nextProfile))
        setMsg('头像更新成功')
      } catch (err) {
        setError('头像更新失败')
      } finally {
        e.target.value = ''
      }
    }

    const logout = () => {
      localStorage.removeItem('user_token')
      localStorage.removeItem('user_profile')
      router.replace('/index')
    }

    const noticeNowTick = ref(Date.now())
    let noticeTimer = null
    let noticeRefreshTimer = null
    let notificationTimer = null

    const getTimePeriodText = (hour) => {
      const h = Number(hour)
      if (Number.isNaN(h)) return '你好'
      if (h >= 0 && h < 6) return '凌晨好'
      if (h >= 6 && h < 11) return '早上好'
      if (h >= 11 && h < 13) return '中午好'
      if (h >= 13 && h < 18) return '下午好'
      return '晚上好'
    }

    const showTimeGreeting = () => {
      try {
        const now = new Date()
        const greet = getTimePeriodText(now.getHours())
        const nickname = (profile.value && profile.value.nickname) ? String(profile.value.nickname).trim() : ''
        const who = nickname || '旅行者'
        showToast(`${who}，${greet}！`)
      } catch (e) {
        // ignore
      }
    }

    // 底部 Toast：统一由上方的 `showToast/toastActive/toastMessage` 驱动
    // 这里不再直接操作 DOM，避免与组件内 Toast 重复/冲突

    const refreshExpiredNoticesOnce = async () => {
      try {
        const now = new Date(noticeNowTick.value)
        if (Array.isArray(myNotices.value) && myNotices.value.some((x) => x && x.expireAt && new Date(x.expireAt) <= now)) {
          await loadMyNotices()
        }
      } catch (e) {
        // ignore
      }
    }

    onMounted(async () => {
      window.addEventListener('keydown', onGlobalKeydown)

      await loadProfile()
      await loadNotifications()
      await loadMyBuildings()
      await loadMyNotices()

      // 显示时间段问候弹窗
      showTimeGreeting()

      // Minecraft 时钟（每秒刷新）
      updateMinecraftClock()
      clockTimer = setInterval(() => {
        updateMinecraftClock()
      }, 1000)

      // 玩家状态栏（轻微动态）
      tickPlayerStatus()
      statusTimer = setInterval(() => {
        tickPlayerStatus()
      }, 1000)

      // 今日冒险情报
      updateDailyIntel()

      noticeTimer = setInterval(() => {
        noticeNowTick.value = Date.now()
      }, 1000)

      noticeRefreshTimer = setInterval(() => {
        refreshExpiredNoticesOnce()
      }, 60000)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', onGlobalKeydown)
      if (oldTimer) clearInterval(oldTimer)
      if (newTimer) clearInterval(newTimer)
      if (clockTimer) clearInterval(clockTimer)
      if (statusTimer) clearInterval(statusTimer)
      if (noticeTimer) clearInterval(noticeTimer)
      if (noticeRefreshTimer) clearInterval(noticeRefreshTimer)
      if (notificationTimer) clearTimeout(notificationTimer)
    })

    return {
      profile,
      avatarSrc,
      fileInput,
      msg,
      error,
      notifyError,
      notifySuccess,
      toastActive,
      toastMessage,
      showToast,
      onAvatarClick,
      handleFile,
      logout,
      emailModalActive,
      emailStep,
      oldEmailCode,
      newEmail,
      newEmailCode,
      newEmailHint,
      sendingOld,
      sendingNew,
      oldCountdown,
      newCountdown,
      openEmailModal,
      closeEmailModal,
      passwordModalActive,
      oldPassword,
      newPassword,
      confirmPassword,
      oldPasswordHint,
      newPasswordHint,
      confirmPasswordHint,
      passwordSubmitting,
      openPasswordModal,
      closePasswordModal,
      submitPasswordChange,
      validateNewEmail,
      sendOldEmailCode,
      sendNewEmailCode,
      goNextEmailStep,
      submitEmailChange,

      notifications,
      unreadCount,
      markRead,
      goBuilding,
      handleBuildingNotificationClick,
      buildingSnapshotModalActive,
      buildingSnapshotData,
      buildingSnapshotNotice,
      openBuildingSnapshotModal,
      closeBuildingSnapshotModal,

      myBuildings,
      buildModalActive,
      buildSubmitting,
      editingMyBuilding,
      isSelected,
      selectDate,
      selectToday,
      clearDate,
      showDatePicker,
      currentYear,
      currentMonth,
      toggleDatePicker,
      prevYear,
      nextYear,
      prevMonth,
      nextMonth,
      daysInMonth,
      firstDayOfMonth,
      buildForm,
      buildImagesPreviews,
      openBuildModal,
      openEditBuildModal,
      deleteMyBuilding,
      closeBuildModal,
      handleBuildImagesChange,
      uploadBuildImage,
      removeBuildImage,
      validateBuildingName,
      validateBuildingSubtitle,
      submitBuild,

      myNotices,
      noticeDraft,
      noticeDraftHint,
      editingNoticeId,
      noticeSubmitting,
      remainingNoticeCount,
      formatDateTime,
      formatRemaining,
      remainingBadgeClass,
      validateNoticeDraft,
      noticeModalActive,
      openNoticeModalForCreate,
      openNoticeModalForEdit,
      closeNoticeModal,
      submitNotice,
      editNotice,
      cancelEditNotice,
      deleteNotice,

      isProfilePanelActive,
      isNotificationsPanelActive,

      handleImageDragStart,
      handleImageDragOver,
      handleImageDrop,

      retryBuildUpload,
      removeBuildUploadItem,
      buildUploadQueue,
      currentTimeText,
      mcClockDialY,
      mcClockStatus,
      playerHealth,
      playerHunger,
      playerExpPercent,
      playerLevel,
      dailyFortune,
      dailyWeather,
      dailyMobs,

      imagePreviewUrl,
      imagePreviewActive,
      openImagePreview,
      closeImagePreview,
      getNoticeIcon,
      getBuildingIcon
    }
  }
}
</script>

<style>
@import '../styles/auth-mc.css';

.mc-btn-small {
  padding: 12px 18px;
  border: 3px solid #000;
  font-family: inherit;
  font-size: 15px;
  min-height: 42px;
  background: linear-gradient(180deg, #bdbdbd 0%, #8e8e8e 55%, #6f6f6f 100%);
  color: #000;
  box-shadow:
    inset -3px -3px 0 rgba(0,0,0,0.35),
    inset 3px 3px 0 rgba(255,255,255,0.14),
    0 6px 16px rgba(0,0,0,0.55);
  cursor: pointer;
  transition: transform 0.08s ease, filter 0.12s ease, box-shadow 0.12s ease;
}

.mc-btn-small:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow:
    inset -3px -3px 0 rgba(0,0,0,0.32),
    inset 3px 3px 0 rgba(255,255,255,0.16),
    0 8px 20px rgba(0,0,0,0.60);
}

.mc-btn-small:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow:
    inset -2px -2px 0 rgba(0,0,0,0.32),
    inset 2px 2px 0 rgba(255,255,255,0.12),
    0 4px 12px rgba(0,0,0,0.55);
}

.mc-btn-small.mc-btn-danger {
  background: linear-gradient(180deg, #ff8a8a 0%, #ff6b6b 55%, #e74c3c 100%);
  color: #fff;
}

.mc-btn-small.mc-btn-danger:hover:not(:disabled) {
  filter: brightness(1.1);
  box-shadow:
    inset -3px -3px 0 rgba(0,0,0,0.32),
    inset 3px 3px 0 rgba(255,255,255,0.16),
    0 8px 20px rgba(0,0,0,0.60);
}

.me-hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 18px;
  margin: 10px 0 24px;
  border: 3px solid rgba(255,255,255,0.14);
  background: linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.18) 100%);
  box-shadow: 0 22px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.10);
  backdrop-filter: blur(10px);
  border-radius: 6px;
  position: relative;
  overflow: hidden;
}

.me-hero::before {
  content: '';
  position: absolute;
  inset: -40px;
  background:
    radial-gradient(500px 280px at 20% 35%, rgba(94,168,24,0.22), transparent 65%),
    radial-gradient(420px 240px at 75% 30%, rgba(255,255,255,0.08), transparent 60%);
  pointer-events: none;
}

.me-hero::after {
  content: '';
  position: absolute;
  inset: -30%;
  background:
    linear-gradient(120deg,
      transparent 0%,
      rgba(154,224,84,0.10) 40%,
      rgba(94,168,24,0.18) 50%,
      rgba(154,224,84,0.10) 60%,
      transparent 100%);
  transform: translateX(-40%) translateY(-10%);
  animation: mcEnchantSweep 5.5s ease-in-out infinite;
  pointer-events: none;
  mix-blend-mode: screen;
}

@keyframes mcEnchantSweep {
  0% { transform: translateX(-55%) translateY(-10%) rotate(0deg); opacity: 0.0; }
  15% { opacity: 0.55; }
  50% { transform: translateX(10%) translateY(0%) rotate(1deg); opacity: 0.35; }
  85% { opacity: 0.55; }
  100% { transform: translateX(65%) translateY(10%) rotate(0deg); opacity: 0.0; }
}

@keyframes mcPulseGlow {
  0%, 100% { box-shadow: 0 0 0 1px rgba(0,0,0,0.30), 0 0 10px rgba(154,224,84,0.12); }
  50% { box-shadow: 0 0 0 1px rgba(0,0,0,0.30), 0 0 16px rgba(154,224,84,0.20); }
}

@keyframes mcPulseGlowStrong {
  0%, 100% { box-shadow: 0 0 0 1px rgba(0,0,0,0.35), 0 0 14px rgba(154,224,84,0.18); }
  50% { box-shadow: 0 0 0 1px rgba(0,0,0,0.35), 0 0 22px rgba(154,224,84,0.28); }
}

.me-tag {
  animation: mcPulseGlow 3.6s ease-in-out infinite;
}

.pill-badge {
  animation: mcPulseGlowStrong 2.4s ease-in-out infinite;
}

.me-badge-ok {
  animation: mcPulseGlow 4.2s ease-in-out infinite;
}

.me-badge-warn {
  animation: mcPulseGlow 3.2s ease-in-out infinite;
}

.me-badge-danger {
  animation: mcPulseGlowStrong 2.6s ease-in-out infinite;
}

.me-hero-left {
  display: flex;
  gap: 14px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.me-hero-avatar {
  width: 66px;
  height: 66px;
  border-radius: 4px;
  border: 3px solid rgba(0,0,0,0.55);
  background: rgba(0,0,0,0.35);
  box-shadow: 0 12px 26px rgba(0,0,0,0.55);
  overflow: hidden;
  flex: 0 0 auto;
}

.me-hero-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .auth-topbar.me-topbar {
    padding: 10px 15px;
    height: auto;
    flex-direction: column;
    gap: 15px;
  }

  .topbar-actions {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
  }

  .me-layout-full {
    padding: 10px !important;
  }

  .me-hero {
    flex-direction: column;
    padding: 15px;
  }

  .me-hero-left {
    flex-direction: column;
    text-align: center;
  }

  .me-hero-actions {
    margin-top: 15px;
    width: 100%;
  }

  .layout-main {
    flex-direction: column !important;
  }

  .layout-left, .layout-right {
    width: 100% !important;
  }

  .me-card-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .me-card-head-actions {
    width: 100%;
  }

  .me-card-head-actions .mc-btn {
    width: 100%;
  }

  .me-building-item {
    flex-direction: column;
  }

  .me-building-cover {
    width: 100% !important;
    height: 180px !important;
  }

  .me-drawer-panel {
    width: 100vw !important;
    right: -100vw !important;
  }

  .me-drawer-panel.active {
    right: 0 !important;
  }

  .me-hero-title {
    font-size: 16px;
  }

  .me-hero-sub {
    font-size: 11px;
  }
}

.me-hero-title {
  font-size: 18px;
  font-weight: 900;
  color: #fff;
  text-shadow: 3px 3px 0 rgba(0,0,0,0.85);
  letter-spacing: 1px;
}

.me-hero-sub {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255,255,255,0.72);
  line-height: 1.4;
}

.me-hero-tags {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.me-tag {
  font-size: 11px;
  color: rgba(255,255,255,0.85);
  padding: 6px 10px;
  border-radius: 2px;
  border: 2px solid rgba(255,255,255,0.14);
  background: linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 100%);
  backdrop-filter: blur(6px);
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.06),
    0 0 0 1px rgba(0,0,0,0.30);
  text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
}

.me-hero-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-content: center;
  position: relative;
  z-index: 1;
}

.me-card {
  border-radius: 6px;
  border: 3px solid rgba(255,255,255,0.14);
  background: linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.22) 100%);
  box-shadow: 0 18px 50px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08);
  backdrop-filter: blur(10px);
  overflow: hidden;
  position: relative;
}

.me-card::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.16;
  background-image:
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 18px 18px;
  mix-blend-mode: screen;
}

.me-card-compact {
  padding: 14px;
}

.me-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px;
  border-bottom: 2px solid rgba(255,255,255,0.10);
  background: rgba(0,0,0,0.18);
}

.me-card-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.me-card-head-actions .mc-btn {
  min-height: 40px;
}

.me-card-title {
  font-size: 17px;
  font-weight: 900;
  color: #fff;
  text-shadow: 2px 2px 0 rgba(0,0,0,0.85);
  letter-spacing: 1px;
}

.me-card-sub {
  margin-top: 8px;
  font-size: 15px;
  color: rgba(255,255,255,0.85);
  line-height: 1.6;
}

.me-card-body {
  padding: 14px 16px 16px;
}

.me-inline-hint {
  font-size: 11px;
  color: rgba(255,255,255,0.70);
  margin-bottom: 12px;
}

.mc-form-hint {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.2;
  color: #c62828;
  font-weight: 700;
}

.me-empty {
  padding: 18px 12px;
  border: 2px dashed rgba(255,255,255,0.18);
  border-radius: 6px;
  background: rgba(0,0,0,0.18);
}

.me-empty-title {
  font-size: 13px;
  font-weight: 800;
  color: rgba(255,255,255,0.88);
}

.me-empty-sub {
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,0.60);
}

.me-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.me-list-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  border-radius: 12px;
  border: 2px solid rgba(255,255,255,0.10);
  background: rgba(0,0,0,0.20);
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.me-list-item:hover {
  transform: translateY(-1px);
  border-color: rgba(154,224,84,0.40);
  box-shadow: 0 14px 30px rgba(0,0,0,0.45);
}

.me-list-title {
  color: #fff;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.me-list-meta {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.me-meta {
  font-size: 11px;
  color: rgba(255,255,255,0.58);
}

.me-badge {
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.14);
  background: rgba(0,0,0,0.30);
  color: rgba(255,255,255,0.90);
}

.me-badge-ok {
  border-color: rgba(154,224,84,0.35);
  background: rgba(94,168,24,0.20);
}

.me-badge-warn {
  border-color: rgba(255,196,0,0.35);
  background: rgba(255,196,0,0.15);
}

.me-badge-danger {
  border-color: rgba(255,107,107,0.45);
  background: rgba(255,107,107,0.14);
}

.me-badge-neutral {
  border-color: rgba(255,255,255,0.16);
}

.me-list-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.me-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.me-building-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.me-building-item {
  display: flex;
  gap: 14px;
  padding: 14px;
  border: 3px solid rgba(255,255,255,0.12);
  background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.25) 100%);
  box-shadow: 0 12px 30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08);
  backdrop-filter: blur(8px);
  border-radius: 6px;
  position: relative;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.me-building-item:hover {
  transform: translateY(-1px);
  border-color: rgba(154,224,84,0.40);
  box-shadow: 0 14px 36px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.10);
}

.me-building-cover {
  flex: 0 0 160px;
  height: 100px;
  border-radius: 4px;
  border: 3px solid rgba(0,0,0,0.55);
  background: rgba(0,0,0,0.40);
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.me-building-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.2s ease;
}

.me-building-cover:hover img {
  transform: scale(1.05);
}

.me-building-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.55);
  font-size: 12px;
}

.me-building-cover-frame {
  position: absolute;
  inset: -4px;
  border: 4px solid rgba(0,0,0,0.75);
  border-radius: 4px;
  pointer-events: none;
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.12),
    0 0 0 1px rgba(0,0,0,0.30);
}

.me-building-cover::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(0,0,0,0.00) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.55) 100%),
    radial-gradient(220px 120px at 30% 30%, rgba(255,255,255,0.10), transparent 60%);
}

.me-building-cover-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(94,168,24,0.85);
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  padding: 4px 8px;
  border-radius: 2px;
  border: 2px solid rgba(0,0,0,0.55);
  box-shadow: 0 2px 8px rgba(0,0,0,0.55);
  pointer-events: none;
}

.me-building-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  background: linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.32) 100%);
  border: 2px solid rgba(0,0,0,0.55);
  border-radius: 4px;
  padding: 12px;
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.06),
    0 0 0 1px rgba(0,0,0,0.30);
}

.me-building-body::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.08;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size: 12px 12px;
  mix-blend-mode: screen;
}

.me-building-title {
  color: #fff;
  font-size: 14px;
  font-weight: 900;
  text-shadow: 2px 2px 0 rgba(0,0,0,0.85);
}

.me-building-sub {
  margin-top: 6px;
  color: rgba(255,255,255,0.65);
  font-size: 12px;
  line-height: 1.4;
}

.me-building-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 12px;
}

.me-notice-actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.me-tip-text {
  padding: 12px 16px 16px;
  font-size: 13px;
  color: rgba(255,255,255,0.78);
  line-height: 1.7;
}

.me-tip-text > div {
  padding: 4px 0;
}

@media (max-width: 1024px) {
  .me-hero {
    flex-direction: column;
  }

  .me-hero-actions {
    grid-template-columns: 1fr 1fr;
  }

  .me-building-item {
    flex-direction: column;
  }

  .me-building-cover {
    width: 100%;
    flex: 0 0 auto;
    height: 140px;
  }
}

.me-notice-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.me-notice-item {
  display: grid;
  grid-template-columns: 54px 1fr;
  gap: 12px;
  padding: 12px;
  border: 3px solid rgba(255,255,255,0.12);
  background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.25) 100%);
  box-shadow: 0 12px 30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08);
  backdrop-filter: blur(8px);
  border-radius: 6px;
  position: relative;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.me-notice-item:hover {
  transform: translateY(-1px);
  border-color: rgba(154,224,84,0.40);
  box-shadow: 0 14px 36px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.10);
}

.me-notice-item:hover .me-notice-icon {
  transform: translateY(-1px) scale(1.04);
}

.me-notice-icon {
  transition: transform 0.12s ease;
}

.me-notice-left {
  position: relative;
  border: 3px solid rgba(0,0,0,0.55);
  border-radius: 4px;
  background: rgba(0,0,0,0.35);
  box-shadow: inset -3px -3px 0 rgba(0,0,0,0.30), inset 3px 3px 0 rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.me-notice-left::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.10;
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 10px 10px;
  mix-blend-mode: screen;
}

.me-notice-pin {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 10px;
  height: 10px;
  background: rgba(154,224,84,0.95);
  border: 2px solid rgba(0,0,0,0.60);
  box-shadow: 0 0 12px rgba(154,224,84,0.25);
}

.me-notice-icon {
  font-size: 18px;
  filter: drop-shadow(2px 2px 0 rgba(0,0,0,0.85));
}

.me-notice-body {
  position: relative;
  background: linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.32) 100%);
  border: 2px solid rgba(0,0,0,0.55);
  border-radius: 4px;
  padding: 12px;
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.06),
    0 0 0 1px rgba(0,0,0,0.30);
}

.me-notice-body::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.08;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size: 12px 12px;
  mix-blend-mode: screen;
}

.me-notice-title {
  color: #fff;
  font-size: 13px;
  line-height: 1.6;
  text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
  white-space: pre-wrap;
}

.me-notice-meta {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.me-notice-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.mc-image-remove-btn {
  position: absolute;
  top: 4px;
  left: 4px;
  background: #555;
  color: #fff;
  border: 2px solid #000;
  padding: 2px 6px;
  font-size: 10px;
  cursor: pointer;
}

.mc-image-remove-btn:hover {
  background: #777;
}

/* Minecraft 时钟卡片 */
.me-card-clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
}

.mc-clock-outer {
  width: 160px;
  height: 160px;
  background: #D9A32E;
  border: 8px solid #373737;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  box-shadow: inset -8px -8px 0 #7B5915;
  image-rendering: pixelated;
}

.mc-clock-wrap .dial {
  position: absolute;
  width: 100%;
  height: 300%;
  background: linear-gradient(
    to bottom,
    #000033 0%,
    #4466FF 25%,
    #88AAFF 50%,
    #FF8833 75%,
    #000033 100%
  );
  top: 0;
  transition: transform 1s linear;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.mc-clock-wrap .sun {
  width: 56px;
  height: 56px;
  background: #FFFF00;
  box-shadow: 0 0 15px #FFAA00;
}

.mc-clock-wrap .moon {
  width: 48px;
  height: 48px;
  background: #DDDDDD;
  box-shadow: -8px 0 0 #AAAAAA inset;
}

.mc-clock-status {
  margin-top: 8px;
  font-size: 11px;
  color: rgba(255,255,255,0.75);
  text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
}

.me-player-status .me-card-sub {
  text-align: center;
}

.mc-status-bars {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mc-status-bar {
  border: 3px solid rgba(0,0,0,0.55);
  border-radius: 4px;
  background: rgba(0,0,0,0.25);
  padding: 10px;
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.25), inset 2px 2px 0 rgba(255,255,255,0.06);
}

.mc-status-label {
  font-size: 11px;
  color: rgba(255,255,255,0.85);
  text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
}

.mc-status-track {
  height: 12px;
  margin-top: 8px;
  border: 3px solid #000;
  background: rgba(0,0,0,0.65);
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.35), inset 2px 2px 0 rgba(255,255,255,0.08);
  position: relative;
  overflow: hidden;
}

.mc-status-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.mc-status-bar.health .mc-status-fill {
  background: linear-gradient(180deg, #ff6b6b 0%, #e74c3c 55%, #c0392b 100%);
}

.mc-status-bar.hunger .mc-status-fill {
  background: linear-gradient(180deg, #ffb142 0%, #ff9f1a 55%, #e67e22 100%);
}

.mc-status-bar.exp .mc-status-fill {
  background: linear-gradient(180deg, #9ae054 0%, #5ea818 55%, #3c6b10 100%);
}

.mc-status-value {
  margin-top: 8px;
  font-size: 11px;
  color: rgba(255,255,255,0.78);
  display: flex;
  justify-content: flex-end;
}

.me-daily .me-card-sub {
  text-align: center;
}

.mc-daily-grid {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.mc-daily-item {
  border: 3px solid rgba(0,0,0,0.55);
  border-radius: 4px;
  background: rgba(0,0,0,0.25);
  padding: 10px;
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.25), inset 2px 2px 0 rgba(255,255,255,0.06);
}

.mc-daily-k {
  font-size: 11px;
  color: rgba(255,255,255,0.85);
  text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
  margin-bottom: 6px;
}

.mc-daily-v {
  font-size: 12px;
  color: #9ae054;
  text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
  line-height: 1.4;
}

.mc-item-icon {
  margin-right: 8px;
  font-size: 16px;
  filter: drop-shadow(1px 1px 0 rgba(0,0,0,0.85));
}


.mc-clock-wrap {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.mc-clock-img {
  width: 64px;
  height: 64px;
  object-fit: contain;
  border: 3px solid #000;
  border-radius: 4px;
  background: rgba(0,0,0,0.35);
  box-shadow: 0 8px 20px rgba(0,0,0,0.55), inset -2px -2px 0 rgba(0,0,0,0.30), inset 2px 2px 0 rgba(255,255,255,0.08);
  animation: mcClockFloat 4s ease-in-out infinite;
}

@keyframes mcClockFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
}

.mc-clock-time {
  font-size: 14px;
  font-weight: 900;
  color: #9ae054;
  text-shadow: 1px 1px 0 #000, 0 0 8px rgba(154,224,84,0.4);
  letter-spacing: 1px;
  font-family: 'Courier New', monospace;
}

/* /me 页面专用 Toast（仿 /index 拍一拍提示） */
#me-toast {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: -120px;
  background: #000;
  border: 4px solid #fff;
  padding: 20px 40px;
  z-index: 99999;
  transition: 0.5s;
  opacity: 0;
  font-family: inherit;
  font-size: 14px;
  color: #fff;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(0,0,0,0.6);
}
#me-toast.active {
  bottom: 60px;
  opacity: 1;
}

/* 统一处理所有弹窗类提示 */
:deep(.el-message), :deep(.el-notification), :deep(.el-message-box__wrapper) {
  z-index: 100000 !important;
}

.mc-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 10px;
  padding: 10px;
  background: #1b1b1b;
  border: 3px solid #000;
  box-shadow: inset -2px -2px 0 #555, inset 2px 2px 0 #2a2a2a;
}

.mc-image-tile {
  position: relative;
  border: 3px solid #000;
  background: #111;
  box-shadow: inset -2px -2px 0 #444, inset 2px 2px 0 #222;
  padding: 6px;
  cursor: move;
}

.mc-image-tile-cover {
  border: 3px dashed #5ea818;
  background: #1a2e0a;
}

.mc-image-cover-label {
  position: absolute;
  top: 6px;
  left: 6px;
  background: #5ea818;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border: 2px solid #000;
  pointer-events: none;
}

.mc-image-thumb {
  width: 100%;
  height: 74px;
  object-fit: cover;
  border: 2px solid #000;
  background: #000;
  display: block;
}

.mc-image-del {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border: 2px solid #000;
  padding: 2px 6px;
  font-size: 10px;
}

.mc-image-del:hover {
  background: #a00;
}

.mc-image-uploading {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.mc-image-uploading-item {
  position: relative;
  width: 100px;
  height: 100px;
  border: 2px dashed #666;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mc-image-uploading-item img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.mc-image-uploading-label {
  position: absolute;
  bottom: 4px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 10px;
  color: #9ae054;
  background: rgba(0,0,0,0.7);
  padding: 2px;
}

/* /me 页面覆盖 auth-mc 的居中小盒子布局（仅影响本组件） */
.user-auth-container {
  align-items: stretch;
  justify-content: stretch;
  padding: 0;
}

.auth-wrap {
  width: 100%;
  max-width: none;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* =================================== */
/* 全屏两栏主舞台 + 抽屉面板样式 */
/* =================================== */
.me-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.topbar-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border: 4px solid #000;
  background: linear-gradient(180deg, #bdbdbd 0%, #8e8e8e 55%, #6f6f6f 100%);
  box-shadow:
    inset -4px -4px 0 rgba(0,0,0,0.35),
    inset 4px 4px 0 rgba(255,255,255,0.14),
    0 10px 24px rgba(0,0,0,0.55);
  cursor: pointer;
  transition: transform 0.08s ease, filter 0.12s ease, box-shadow 0.12s ease;
  position: relative;
}

.topbar-pill:hover {
  filter: brightness(1.06);
  box-shadow:
    inset -4px -4px 0 rgba(0,0,0,0.32),
    inset 4px 4px 0 rgba(255,255,255,0.16),
    0 12px 30px rgba(0,0,0,0.60);
}

.topbar-pill:active {
  transform: translateY(1px);
  box-shadow:
    inset -3px -3px 0 rgba(0,0,0,0.32),
    inset 3px 3px 0 rgba(255,255,255,0.12),
    0 8px 18px rgba(0,0,0,0.55);
}

.topbar-pill.active-pill {
  border-color: rgba(154,224,84,1);
  box-shadow:
    0 0 0 3px rgba(154,224,84,0.20),
    0 0 26px rgba(154,224,84,0.18),
    inset -4px -4px 0 rgba(0,0,0,0.32),
    inset 4px 4px 0 rgba(255,255,255,0.16);
}

.pill-icon {
  font-size: 18px;
  line-height: 1;
}

.pill-text {
  font-size: 12px;
  font-weight: bold;
  color: #000;
}

.pill-avatar {
  width: 24px;
  height: 24px;
  border: 2px solid #000;
  border-radius: 2px;
  object-fit: cover;
  background: #111;
}

.pill-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  background: #e74c3c;
  border: 2px solid #000;
  color: #fff;
  font-size: 9px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset -1px -1px 0 #c0392b, inset 1px 1px 0 #ec7063;
}

/* 全屏两栏布局 */
.me-layout-full {
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  padding: 0 40px 40px;
  box-sizing: border-box;
}

.layout-header {
  margin-bottom: 20px;
}

.layout-main {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(760px, 1fr) minmax(360px, 460px);
  gap: 40px;
  flex: 1;
  align-items: start;
}

.layout-left {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 34px;
}

.layout-right {
  position: sticky;
  top: 110px;
  min-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

/* 数据看板 */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 12px;
}

.stat {
  background: rgba(0, 0, 0, 0.3);
  border: 3px solid #4a4a4a;
  padding: 12px;
  text-align: center;
}

.stat-k {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
}

.stat-v {
  font-size: 20px;
  font-weight: bold;
  color: #9ae054;
  text-shadow: 1px 1px 2px #000;
}

/* 抽屉式面板 */
.me-drawer-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  height: 100vh;
  background: rgba(0, 0, 0, 0.92);
  border-left: 4px solid #5a5a5a;
  box-shadow: -8px 0 24px rgba(0,0,0,0.6);
  z-index: 1000;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.2, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
}

.me-drawer-panel.active {
  transform: translateX(0);
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #3a3a3a;
  border-bottom: 3px solid #5a5a5a;
}

.drawer-title {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  text-shadow: 1px 1px 2px #000;
}

.drawer-close {
  width: 36px;
  height: 36px;
  border: 3px solid #000;
  background: #8e8e8e;
  box-shadow: inset -2px -2px 0 #555, inset 2px 2px 0 #ddd;
  color: #000;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.drawer-body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

/* 响应式优化 */
@media (max-width: 1024px) {
  .layout-main {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .layout-right {
    order: -1;
  }

  .stat-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .me-topbar {
    flex-direction: column;
    gap: 12px;
    padding: 20px;
  }

  .topbar-actions {
    width: 100%;
    justify-content: space-between;
  }

  .me-layout-full {
    padding: 0 20px 20px;
  }

  .me-drawer-panel {
    width: 100vw;
  }

  .stat-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .stat {
    padding: 10px;
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  margin-left: 8px;
  background: #e74c3c;
  border: 3px solid #000;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  box-shadow: inset -2px -2px 0 #c0392b, inset 2px 2px 0 #ec7063;
}

.dot {
  width: 10px;
  height: 10px;
  background: #9ae054;
  border: 2px solid #000;
  box-shadow: inset -1px -1px 0 rgba(0,0,0,0.3);
  flex: 0 0 auto;
}

.data-item-unread {
  border-color: #9ae054;
}

.profile {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 16px;
  margin-top: 14px;
}

.avatar-box {
  border: 4px solid #000;
  background: rgba(0,0,0,0.35);
  box-shadow: inset -4px -4px 0 rgba(0,0,0,0.35), inset 4px 4px 0 rgba(255,255,255,0.06);
  padding: 10px;
  cursor: pointer;
}

.avatar {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border: 3px solid #fff;
  background: #111;
}

.avatar-tip {
  margin-top: 8px;
  font-size: 11px;
  color: rgba(255,255,255,0.65);
  text-align: center;
}

.profile-info .kv {
  display: grid;
  grid-template-columns: 86px 1fr;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 2px solid rgba(255,255,255,0.08);
}

.profile-info .k {
  font-size: 12px;
  color: rgba(255,255,255,0.7);
}

.profile-info .v {
  font-size: 12px;
  color: #fff;
  word-break: break-word;
}

.profile-actions {
  display: flex;
  gap: 10px;
}

.profile-actions .mc-btn {
  flex: 1;
}

.email-steps {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.step-title {
  padding: 8px 10px;
  border: 3px solid #000;
  background: #8e8e8e;
  box-shadow: inset -2px -2px 0 #555, inset 2px 2px 0 #ddd;
  font-size: 11px;
  color: #000;
  text-align: center;
}

.step-title.on {
  background: var(--mc-green);
  box-shadow: inset -3px -3px 0 #3c6b10, inset 3px 3px 0 #9ae054;
}

.code-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: end;
}

.code-send {
  min-width: 128px;
}

/* Toast（与首页问好弹窗一致：底部中间弹出并自动收回） */
.me-toast {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: -120px;
  background: #000;
  border: 4px solid #fff;
  padding: 20px 40px;
  z-index: 2000000 !important;
  transition: 0.5s;
  opacity: 0;
}

/* 确保所有模态框层级低于提示框（避免遮挡 toast） */
.mc-modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: all 0.3s ease;
  overflow-y: auto; /* 允许遮罩层滚动 */
}

.mc-modal-mask.active {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.mc-modal {
  display: none; /* 默认完全隐藏，防止在页面流中占位 */
  position: relative;
  background: #c6c6c6;
  border: 6px solid #000;
  padding: 25px;
  z-index: 1000001;
  box-shadow: inset -4px -4px 0 #555, inset 4px 4px 0 #fff;
  flex-direction: column;
  width: 550px;
  max-width: 100%;
  max-height: none; /* 让遮罩层控制滚动 */
  box-sizing: border-box;
}

/* 仅在遮罩层激活时显示弹窗 */
.mc-modal-mask.active .mc-modal {
  display: flex;
  animation: mcPop 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28);
}

@keyframes mcPop {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.mc-modal-scroll {
  flex: 1;
  margin: 10px 0;
}

.modal-btns {
  display: flex;
  gap: 10px;
  padding-top: 15px;
  border-top: 3px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .mc-modal-mask {
    padding: 10px;
    align-items: flex-start; /* 手机端顶部对齐 */
    padding-top: 20px;
    padding-bottom: 40px; /* 留出底部空间防止截断 */
  }

  .mc-modal {
    width: 100%;
    padding: 15px;
    border-width: 4px;
    gap: 8px;
  }

  /* 调整移动端表单密度 */
  .mc-modal h3 {
    font-size: 13px !important;
    margin-bottom: 5px !important;
  }

  .mc-modal .mc-input {
    padding: 10px;
    font-size: 12px;
  }

  .mc-modal .modal-btns {
    padding-top: 10px;
  }

  .mc-modal .mc-btn {
    padding: 12px;
    font-size: 13px;
  }
}

/* Element Plus 提示同样必须在 modal 之上 */
:deep(.el-message), :deep(.el-notification), :deep(.el-message-box__wrapper) {
  z-index: 2000001 !important;
}
.me-toast.active {
  bottom: 60px;
  opacity: 1;
}


/* Minecraft 风格未知按钮 */
/* 自定义像素风日期选择器面板 */
.mc-datepicker-container {
  position: relative;
  flex: 1;
}

.mc-datepicker-panel {
  position: absolute;
  top: 100%;
  left: 0;
  width: 280px;
  background: #1e1e1e;
  border: 4px solid #3c3c3c;
  box-shadow: 0 8px 16px rgba(0,0,0,0.8), inset 0 0 0 2px #000;
  z-index: 2000;
  margin-top: 8px;
  padding: 10px;
  font-family: 'Press Start 2P', cursive;
  image-rendering: pixelated;
}

.mc-datepicker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 2px solid #3c3c3c;
}

.mc-datepicker-btn {
  background: #3c3c3c;
  color: #fff;
  border: none;
  padding: 4px 8px;
  font-size: 10px;
  cursor: pointer;
}

.mc-datepicker-btn:hover {
  background: #5ea818;
}

.mc-datepicker-title {
  font-size: 10px;
  color: #5ea818;
}

.mc-datepicker-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 8px;
  color: #888;
  margin-bottom: 5px;
}

.mc-datepicker-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.mc-datepicker-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: #fff;
  cursor: pointer;
  border: 2px solid transparent;
}

.mc-datepicker-day:hover {
  background: #3c3c3c;
  border-color: #5ea818;
}

.mc-datepicker-day.active {
  background: #5ea818;
  color: #000;
}

.mc-datepicker-day.empty {
  cursor: default;
}

.mc-datepicker-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 2px solid #3c3c3c;
}

.mc-datepicker-footer-btn {
  flex: 1;
  background: #3c3c3c;
  color: #fff;
  border: none;
  padding: 6px 8px;
  font-size: 9px;
  cursor: pointer;
  text-align: center;
}

.mc-datepicker-footer-btn:hover {
  background: #5ea818;
  color: #000;
}

.mc-datepicker-footer-btn.clear:hover {
  background: #ff3b30;
  color: #000;
}

.date-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 15px;
}

.mc-unknown-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 4px solid #000;
  background: linear-gradient(180deg, #8b8b8b 0%, #6a6a6a 55%, #4a4a4a 100%);
  box-shadow: 
    inset -4px -4px 0 rgba(0,0,0,0.4),
    inset 4px 4px 0 rgba(255,255,255,0.15),
    0 8px 20px rgba(0,0,0,0.6);
  cursor: pointer;
  transition: transform 0.08s ease, filter 0.12s ease, box-shadow 0.12s ease;
  position: relative;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  min-width: 120px;
  justify-content: center;
}

.mc-unknown-btn:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 
    inset -4px -4px 0 rgba(0,0,0,0.35),
    inset 4px 4px 0 rgba(255,255,255,0.18),
    0 10px 26px rgba(0,0,0,0.65);
  transform: translateY(-1px);
}

.mc-unknown-btn:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow: 
    inset -3px -3px 0 rgba(0,0,0,0.35),
    inset 3px 3px 0 rgba(255,255,255,0.12),
    0 6px 16px rgba(0,0,0,0.55);
}

.mc-unknown-btn.active {
  background: linear-gradient(180deg, #5ea818 0%, #4a8c14 55%, #366b0f 100%);
  border-color: #2d4a0b;
  box-shadow: 
    0 0 0 3px rgba(94,168,24,0.25),
    0 0 20px rgba(94,168,24,0.20),
    inset -4px -4px 0 rgba(45,74,11,0.4),
    inset 4px 4px 0 rgba(154,224,84,0.25);
  animation: mcUnknownGlow 2s ease-in-out infinite;
}

@keyframes mcUnknownGlow {
  0%, 100% { 
    box-shadow: 
      0 0 0 3px rgba(94,168,24,0.25),
      0 0 20px rgba(94,168,24,0.20),
      inset -4px -4px 0 rgba(45,74,11,0.4),
      inset 4px 4px 0 rgba(154,224,84,0.25);
  }
  50% { 
    box-shadow: 
      0 0 0 3px rgba(94,168,24,0.35),
      0 0 28px rgba(94,168,24,0.30),
      inset -4px -4px 0 rgba(45,74,11,0.4),
      inset 4px 4px 0 rgba(154,224,84,0.25);
  }
}

.mc-unknown-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #000;
  color: #fff;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 0 4px rgba(255,255,255,0.8);
  border: 2px solid #444;
  box-shadow: 
    inset 0 0 0 1px rgba(255,255,255,0.3),
    0 0 8px rgba(0,0,0,0.4);
}

.mc-unknown-text {
  font-size: 12px;
  color: #fff;
  text-shadow: 2px 2px 0 #000;
  letter-spacing: 0.5px;
}

.mc-unknown-btn.active .mc-unknown-icon {
  background: #5ea818;
  border-color: #2d4a0b;
  animation: mcUnknownIconPulse 1.5s ease-in-out infinite;
}

@keyframes mcUnknownIconPulse {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 
      inset 0 0 0 1px rgba(255,255,255,0.3),
      0 0 8px rgba(94,168,24,0.6);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 
      inset 0 0 0 1px rgba(255,255,255,0.5),
      0 0 12px rgba(94,168,24,0.8);
  }
}

@media (max-width: 620px) {
  .profile {
    grid-template-columns: 1fr;
  }
  .avatar {
    height: 180px;
  }
  .code-row {
    grid-template-columns: 1fr;
  }
  .code-send {
    width: 100%;
  }

  .date-input-group {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .mc-unknown-btn {
    min-width: auto;
    width: 100%;
  }
}
  /* 图片预览遮罩 */
  .mc-image-preview {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000000;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(4px);
  }

  .mc-image-preview.active {
    opacity: 1;
    pointer-events: auto;
  }

  .mc-image-preview-content {
    position: relative;
    max-width: 90%;
    max-height: 90%;
    transform: scale(0.9);
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mc-image-preview.active .mc-image-preview-content {
    transform: scale(1);
  }

  .mc-image-preview-img {
    max-width: 100%;
    max-height: 90vh;
    border: 4px solid #fff;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
    object-fit: contain;
    image-rendering: auto;
  }

  .mc-image-preview-close {
    position: absolute;
    top: -40px;
    right: -40px;
    width: 40px;
    height: 40px;
    background: #fff;
    border: none;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    transition: all 0.2s;
    color: #333;
  }

  .mc-image-preview-close:hover {
    transform: rotate(90deg) scale(1.1);
    background: #f0f0f0;
  }

  @media (max-width: 768px) {
    .mc-image-preview-close {
      top: -50px;
      right: 0;
    }
  }

</style>
